import { de } from "./de";

export type Language = "en" | "ru" | "de";

export const translations = {
  en: {
    // Navigation
    nav: {
      articles: "Learn",
      games: "Train Your Brain",
      exercises: "Feel Better Now",
      questionnaires: "Check-in",
      contact: "Contact",
    },
    
    // Home page
    home: {
      hero: {
        title1: "Oh Mind Guide",
        title2: "Where neuroscience meets",
        title3: "everyday life in a colourful",
        title4: "playground for your mind",
        subtitle: "Articles, games, exercises, and questionnaires to build brain resilience and support your quality of life – evidence-based and easy to use.",
        exploreBtn: "Explore Oh Mind Guide",
        contactBtn: "Contact Us",
      },
      heroCircle: {
        centerText: "Choose your own path",
        learn: "Learn",
        learnHelper: "Short, clear explanations of brain science and mental wellbeing",
        trainBrain: "Train your brain",
        trainBrainHelper: "Playful challenges to train attention, memory, and flexibility",
        feelBetter: "Feel better now",
        feelBetterHelper: "Guided micro-exercises for stress relief and sharper focus",
        checkIn: "Check-in",
        checkInHelper: "Science-based self-reflection tools for mood, sleep, and stress",
      },
      about: {
        title: "What is Oh Mind Guide?",
        p1: "Oh Mind Guide is a playful, science-driven space to explore how your brain and mind work – and to keep them active, flexible, and resilient.",
        p2: "We turn insights from neuroscience and psychology into clear explanations and practical tools you can use every day – through articles, games, exercises, and questionnaires.",
        p3: "",
        brainResilience: "Brain Resilience",
        brainResilienceDesc: "Train attention, memory, and flexibility.",
        qualityOfLife: "Quality of Life",
        qualityOfLifeDesc: "Support mood, energy, and everyday functioning.",
        scienceBased: "Science-Based",
        scienceBasedDesc: "Rooted in current neuroscience and psychology research.",
        playfulLearning: "Playful Learning",
        playfulLearningDesc: "Fun, engaging, and designed to stick.",
      },
      founder: {
        title: "About Me",
        name: "Margarita Popelnyukhova",
        subtitle: "Medical Doctor (MD), MSc Translational Neuroscience, and founder of Oh Mind Guide.",
        description: "I combine clinical experience with research in brain science to design tools that are both playful and evidence-based. I am dedicated to pushing the boundaries of brain science and translating complex neuroscience into simple, engaging experiences that fit into everyday life.",
        badge1: "Medical Doctor (MD)",
        badge2: "MSc Translational Neuroscience",
        badge3: "Founder of Oh Mind Guide",
      },
      who: {
        title: "Who Oh Mind Guide Is For",
        parents: "Parents & Educators",
        parentsDesc: "Support kids and teens with meaningful brain-friendly activities and conversations.",
        students: "Students",
        studentsDesc: "Strengthen focus, learning, and mental stamina with practical, science-based tools.",
        adults: "Curious Adults",
        adultsDesc: "Keep your mind flexible, creative, and engaged in a fun, accessible way.",
        older: "Older Adults",
        olderDesc: "Maintain cognitive function and stay mentally active with gentle, engaging exercises.",
      },
      what: {
        title: "What We Create",
        subtitle: "Everything is grounded in scientifically based techniques, inspired by current research in neuroscience and psychology.",
        articlesTitle: "Learn",
        articlesDesc: "Short, clear explanations of brain science, brain-related conditions, and mental wellbeing – translated into everyday language.",
        articlesLink: "Start Learning →",
        gamesTitle: "Train Your Brain",
        gamesDesc: "Playful challenges to train attention, memory, flexibility, and problem-solving.",
        gamesLink: "Start Training →",
        exercisesTitle: "Feel Better Now",
        exercisesDesc: "Guided micro-exercises for stress relief, sharper focus, and overall brain health.",
        exercisesLink: "Try Exercises →",
        questionnairesTitle: "Check-in",
        questionnairesDesc: "Science-based self-reflection tools to check in on your mood, anxiety, sleep, and stress levels.",
        questionnairesLink: "Start Check-in →",
        cafeTitle: "OMG Café",
        cafeDesc: "Order a drink, memorise words while it brews, and see how many you can recall. A cozy memory ritual.",
        cafeLink: "Visit Café →",
        photoBoothTitle: "Photo Booth",
        photoBoothDesc: "A tool to capture moments. Take 4 quick snapshots and get a photo strip to keep.",
        photoBoothLink: "Take Photos →",
      },
      whatsNext: {
        title: "What's Next",
        dementiaGuideTitle: "Family Guide for Dementia",
        dementiaGuideDesc: "A downloadable, printable guide for relatives of people living with dementia, with clear explanations of brain and behaviour changes, practical tips for everyday situations, and gentle exercises to support both the person and the caregiver.",
        dementiaGuideStatus: "In progress",
        podcastTitle: "\"From people to people\"",
        podcastDesc: "Video / podcast series.",
        podcastStatus: "Coming soon",
        trainerTitle: "Interhemispheric interaction trainer",
        trainerDesc: "A series of playful exercises designed to strengthen communication between the left and right sides of the brain – supporting coordination, attention, and cognitive flexibility.",
        trainerStatus: "In development",
      },
      contact: {
        title: "Let's Connect",
        subtitle: "Have a collaboration idea, a project, or a question? I'd love to hear from you. Get in touch and let's see what we can create together.",
        emailBtn: "Email Oh Mind Guide",
      },
      faq: {
        title: "Frequently Asked Questions",
        q1: "What is Oh Mind Guide?",
        a1: "Oh Mind Guide is a science-based project that turns complex neuroscience, psychology, and medical research into clear, usable knowledge for everyday life. It's a bridge between scientific research and real life: instead of vague \"mental health tips,\" you'll find evidence-based articles, short games and exercises, and practical check-ins (questionnaires) that help you understand your mind, brain, and habits—without the jargon. Oh Mind Guide isn't about \"fixing\" you. It's about learning how your mind works, building better tools over time, and staying curious. You're still in progress.",
        q2: "Is the content scientifically backed?",
        a2: "Yes — the content on Oh Mind Guide is designed to be scientifically backed. Articles are written using reliable neuroscience, psychology, and medical research, and sources are provided whenever possible. Games and exercises are built around evidence-based principles of how the brain learns, focuses, remembers, and regulates stress. Questionnaires are chosen for having published validation (or strong clinical/research use), and each one is presented as a check-in tool — not a diagnosis. Note: Oh Mind Guide is for education and self-understanding and does not replace professional medical advice or treatment.",
        q3: "Who creates the content?",
        a3: "All content on Oh Mind Guide is created by Margarita Popelnyukhova — a medical doctor with an MSc in Translational Neuroscience and the founder of Oh Mind Guide.",
        q4: "Is Oh Mind Guide free to use?",
        a4: "Yes, all articles, brain games, exercises, and questionnaires on Oh Mind Guide are completely free to access. We believe everyone should have access to evidence-based brain health resources.",
        q5: "How do you choose sources and research for Oh Mind Guide?",
        a5: "I rely on high-quality, evidence-based sources. When possible, I prioritize systematic reviews and meta-analyses, large well-designed studies, and respected clinical guidelines or consensus statements. I also check whether results are replicated, how strong the effect is, and whether the findings apply to everyday life. For mental health questionnaires, I use validated, widely used tools and link to the original sources. I aim to summarize the science accurately in plain language, and I avoid making claims that go beyond the evidence.",
        q6: "How often is the content updated?",
        a6: "Key pages are reviewed regularly and updated when new reliable evidence appears. As a baseline, I aim to review and refresh core guides and the most visited pages at least every 1–2 months, and I update older articles when important new studies or guidelines change the picture. When an update happens, I revise the sources and adjust the takeaways so they stay current and accurate. You'll see a \"Last updated\" date on pages that have been reviewed.",
      },
      footer: {
        copyright: "© {year} OMG GTM Solutions L.L.C-FZ. All rights reserved.",
      },
      newHere: {
        title: "New here?",
        whatIsTitle: "What is Oh Mind Guide",
        whatIsDesc: "A science-based playground for cognitive wellbeing.",
        whatIsLink: "Learn about our mission",
        aboutTitle: "About the founder",
        aboutDesc: "Margarita Popelnyukhova, MD + MSc Translational Neuroscience.",
        aboutLink: "Read my story",
        faqTitle: "FAQ & Standards",
        faqDesc: "How content is selected, how often it's updated, disclaimers.",
        faqLink: "See FAQ",
      },
      backToHome: "Back to Home",
    },
    
    // Articles page
    articles: {
      title: "Learn",
      subtitle: "Short, clear explanations of brain science, brain-related conditions, and mental wellbeing – translated into everyday language.",
      // Category groups for filtering
      categories: {
        lifestyle: "Lifestyle",
        foundations: "Foundations",
        brainMentalHealth: "Brain & Mental Health",
      },
      backToAllArticles: "Back to all articles",
      readArticle: "Read Article →",
      comingSoon: "Coming soon",
      moreArticles: "More articles coming soon",
      moreArticlesDesc: "We're working on more science-based guides about brain health, learning, mood, and ageing. Stay tuned!",
      filterByTopic: "Filter by topic:",
      disclaimer: "Disclaimer",
      disclaimerText: "These articles are for general education only and do not replace medical advice or an assessment. If you're worried about yourself or someone you care about, please speak to a qualified health professional.",
      clearFilters: "Clear filters",
      
      // Tags
      tags: {
        foundations: "Foundations",
        sleep: "Sleep",
        stress: "Stress",
        mentalHealth: "Mental health",
        movement: "Movement",
        neurodevelopment: "Neurodevelopment",
        ageing: "Ageing",
        neurodegeneration: "Neurodegeneration",
      },
      
      brainAndMind: {
        title: "Brain and Mind",
        desc: "How physical tissue gives rise to thoughts, feelings and self",
        badge1: "Foundations",
        badge2: "Consciousness",
        subtitle: "How physical tissue gives rise to thoughts, feelings and self",
        disclaimer: "This article is for general education only and does not replace medical advice or an assessment. If you're worried about yourself or someone you care about, please speak to a qualified health professional.",
        backToArticles: "Back to all articles",
      },
      dementia: {
        title: "Dementia",
        desc: "What it is, why it happens, and what we can do",
        badge1: "Brain Health",
        badge2: "Aging",
        subtitle: "What it is, why it happens, and what we can do",
        disclaimer: "This article is for general education only and does not replace medical advice or an assessment. If you're worried about yourself or someone you care about, please speak to a qualified health professional.",
        backToArticles: "Back to all articles",
      },
      parkinsons: {
        title: "Parkinson's Disease",
        desc: "Understanding the basics, the science, and the future",
        badge1: "Brain Health",
        badge2: "Movement",
      },
      adhd: {
        title: "ADHD",
        desc: "Beyond \"Hyperactivity\": How ADHD affects the brain",
        badge1: "Brain Health",
        badge2: "Neurodevelopment",
        badge3: "Education",
        subtitle: "Neurodevelopmental condition with attention, hyperactivity-impulsivity challenges",
        disclaimer: "This article is for general education only and does not replace medical advice or an assessment. If you're worried about yourself or someone you care about, please speak to a qualified health professional.",
      },
      sleep: {
        title: "Sleep & the Brain",
        desc: "How sleep supports memory, mood, and brain health across the lifespan",
        badge1: "Brain Health",
        badge2: "Sleep Science",
        subtitle: "Sleep stages, memory consolidation, brain \"cleaning\", and age-specific tips",
        disclaimer: "This article is for general education only and does not replace medical advice or an assessment. If you're worried about yourself or someone you care about, please speak to a qualified health professional.",
      },
      autism: {
        title: "Autism Spectrum Disorder",
        desc: "What it is, what we know about the brain, and how to support autistic people",
        badge1: "Brain Health",
        badge2: "Neurodevelopment",
        subtitle: "What it is, what we know about the brain, and how to support autistic people across life",
        disclaimer: "This article is for general education only and does not replace medical or psychological advice. If you're worried about yourself or someone you care about, please speak to a qualified health professional.",
      },
      schizophrenia: {
        title: "Schizophrenia",
        desc: "Symptoms, brain science, risk factors, and recovery-focused treatment",
        badge1: "Brain Health",
        badge2: "Mental Health",
        subtitle: "Symptoms, brain science, risk factors, and recovery-focused treatment",
        disclaimer: "This article is for general education only and does not replace medical advice. If you're worried about yourself or someone you care about, please speak to a qualified health professional or emergency services in your country.",
      },
      anxiety: {
        title: "Anxiety",
        desc: "When the brain's alarm system gets stuck on \"high\"",
        badge1: "Brain Health",
        badge2: "Mental Health",
        subtitle: "When the brain's alarm system gets stuck on \"high\"",
        disclaimer: "This article is for general education only and does not replace medical advice or psychological care. If you're worried about yourself or someone you care about, please speak to a qualified health professional. If there is any immediate risk of harm, contact emergency services or your local crisis line.",
      },
      depression: {
        title: "Depression",
        desc: "When the brain's mood system gets stuck in low gear",
        badge1: "Brain Health",
        badge2: "Mental Health",
        subtitle: "When the brain's mood system gets stuck in low gear",
        disclaimer: "This article is for general education only and does not replace medical or psychological care. If you or someone you know feels at risk of self-harm or suicide, please seek urgent help from local emergency services or a crisis line.",
      },
      ptsd: {
        title: "PTSD",
        desc: "Understanding trauma, the brain, and pathways to recovery",
        badge1: "Brain Health",
        badge2: "Mental Health",
        subtitle: "Understanding trauma, the brain, and pathways to recovery",
        disclaimer: "This article is for general education only and does not replace medical or psychological care. If you're struggling with trauma symptoms, please reach out to a qualified mental health professional.",
      },
      feelingsEmotions: {
        title: "Feelings & Emotions",
        desc: "How the brain creates and regulates emotional experience",
        badge1: "Foundations",
        badge2: "Mental Health",
        subtitle: "How the brain creates and regulates emotional experience",
        disclaimer: "This article is for general education only and does not replace professional advice.",
      },
      senseOrgans: {
        title: "Sense Organs",
        desc: "How we perceive the world through our senses",
        badge1: "Foundations",
        badge2: "Brain Science",
        subtitle: "How we perceive the world through our senses",
        disclaimer: "This article is for general education only and does not replace professional advice.",
      },
      painNociception: {
        title: "Pain & Nociception",
        desc: "The difference between sensing danger and feeling pain",
        badge1: "Foundations",
        badge2: "Brain Science",
        subtitle: "The difference between sensing danger and feeling pain",
        disclaimer: "This article is for general education only and does not replace medical advice.",
      },
      microbiota: {
        title: "Microbiota & the Nervous System",
        desc: "How gut microbes talk to the brain and influence mood, cognition, and disease",
        badge1: "Foundations",
        badge2: "Brain Science",
        subtitle: "How gut microbes talk to the brain and influence mood, cognition, and disease",
        disclaimer: "This article is for general education only and does not replace medical advice.",
      },
      coffeeTea: {
        title: "Coffee, Tea & the Brain",
        desc: "What caffeine and polyphenols do for alertness, sleep, and long-term brain health",
        badge1: "Foundations",
        badge2: "Lifestyle",
        subtitle: "What caffeine and polyphenols do for alertness, sleep, and long-term brain health",
        disclaimer: "This article is for general education only and does not replace medical advice.",
      },
    },
    
    // Games page
    games: {
      title: "Train Your Brain",
      subtitle: "Playful challenges to train attention, memory, flexibility, and problem-solving in a fun way.",
      // Category groups for filtering
      categories: {
        memory: "Memory",
        attentionInhibition: "Attention & Inhibition",
        speedFluency: "Speed & Fluency",
        visuoSpatial: "Visuo-Spatial",
        fun: "Fun",
      },
      disclaimer: "Disclaimer: These games are for education and fun, not for medical diagnosis. If you have concerns about your health, please talk to a healthcare professional.",
      backToHome: "Back to Home",
      backToGames: "Back",
      playGame: "Play game",
      comingSoon: "Coming soon",
      moreGamesDesc: "More games are on the way! Stay tuned for new challenges.",
      bestScore: "Best score",
      playAgain: "Play Again",
      start: "Start",
      
      reactionDash: {
        title: "Reaction Dash",
        desc: "Test your reflexes with quick button presses",
        duration: "1-3 mins",
        instructions: "Click the button as quickly as you can when it turns green. Don't click too early!",
        startBtn: "Start Game",
        trial: "Trial",
        of: "of",
        waitForGreen: "Wait for green...",
        clickNow: "CLICK NOW!",
        tooEarly: "Too early! This one doesn't count.",
        getReady: "Get ready for next trial...",
        yourResults: "Your Results",
        averageTime: "Average Time",
        bestTime: "Best Time",
        falseStarts: "False starts",
        feedback: {
          excellent: "Lightning fast reflexes!",
          good: "Great reaction time!",
          ok: "Good effort!",
        },
        snapshot: "This is just a snapshot of your processing speed. Many factors affect reaction time.",
        interestingFact: "Interesting fact",
        factText: "In simple reaction time tasks like this, many adults respond in just a few hundred milliseconds. That's less than half a second from seeing a signal to pressing a button. Reaction time can be influenced by sleep, stress, distraction, and even how familiar you are with the task.",
        tipTitle: "Tip to improve",
        tipText: "To support fast and reliable reactions, focus on the basics: good sleep, regular movement, and reducing distractions when you need to react quickly (for example, avoiding phone use when driving or cycling). Practising a skill in a calm, focused way can also help your reactions feel more automatic.",
        playAgain: "Play Again",
        backToGames: "Back",
      },
      colourWord: {
        title: "Colour-Word Challenge",
        desc: "Match colours while ignoring text in this Stroop-inspired task",
        duration: "1-3 mins",
        disclaimer: "Disclaimer: This game is for education and fun, not for medical diagnosis. If you have concerns about your health, please talk to a healthcare professional.",
        howToPlay: "How to Play",
        instructionsText: "You'll see words like RED, BLUE, GREEN in different colours.",
        taskText: "Your task: click the button that matches the colour of the text, not the word.",
        exampleText: "For example, if you see",
        clickText: ", click BLUE.",
        startBtn: "Start",
        trialOf: "Trial {current} of {total}",
        clickInkColor: "Click the ink color:",
        yourResults: "Your Results",
        correct: "Correct",
        mistakes: "Mistakes",
        averageReactionTime: "Average Reaction Time (correct trials)",
        matchingTrials: "Matching trials",
        conflictingTrials: "Conflicting trials",
        excellentControl: "Excellent cognitive control!",
        goodPerformance: "Good performance!",
        challenging: "Challenging, right?",
        suppressUrgeText: "Your brain had to suppress the urge to read the word and instead pay attention to the text colour.",
        interestingFact: "Interesting fact",
        factText: "The \"Stroop effect\" has been used in psychology for many decades to show how our brain automatically reads words, even when we're trying to focus on something else like the colour of the text. It's a simple example of how automatic habits can interfere with what we want to pay attention to.",
        tipTitle: "Tip to improve",
        tipText: "You can support this kind of focus by practising one task at a time (instead of heavy multitasking), giving your brain short breaks, and using small cues like turning off notifications when you need to concentrate on one thing.",
        playAgain: "Play Again",
        backToGames: "Back",
      },
      numberEcho: {
        title: "Number Echo",
        desc: "Remember and recall sequences of numbers",
        duration: "2-5 mins",
        disclaimer: "This activity is for education and self-reflection, not a medical diagnosis.",
        howToPlay: "How to Play",
        instructionsText: "You'll see a sequence of digits appear one at a time.",
        taskText: "Watch carefully and type them back in the same order!",
        startsWithText: "Start with 3 digits. Each time you get it right, the sequence gets longer by one digit.",
        endsText: "The game ends when you make a mistake or reach the maximum length.",
        startGame: "Start Game",
        sequenceLength: "Sequence length:",
        watchCarefully: "Watch carefully...",
        yourTurn: "Your Turn",
        typeDigits: "Type the digits you saw (no spaces)",
        enterDigits: "Enter digits...",
        submit: "Submit",
        yourResults: "Your Results",
        maxDigitSpan: "Maximum Digit Span",
        roundsCompleted: "Rounds Completed",
        goodStart: "Good start!",
        strongMemory: "Strong working memory!",
        impressiveMemory: "Impressive memory capacity!",
        averageSpanText: "Average adult digit span is 5-7 digits. Small variations are completely normal and don't reflect overall intelligence or ability.",
        interestingFact: "Interesting fact",
        factText: "Working memory is the brain system that lets you hold and manipulate a few pieces of information at once, like a mental notepad. Research suggests we can usually keep only a small number of items active at a time, which is why even simple distractions can make us forget a phone number we just heard.",
        tipTitle: "Tip to improve",
        tipText: "To support working memory, try reducing background distractions, breaking information into smaller \"chunks\" (for example 1-9-8-4 as two pairs instead of four separate numbers), and giving your brain enough sleep and short breaks during demanding tasks.",
        playAgain: "Play Again",
        backToGames: "Back",
      },
      wordSprint: {
        title: "Word Sprint",
        desc: "Type as many words as you can in 60 seconds",
        duration: "1-2 mins",
        disclaimer: "Disclaimer: This game is for education and fun, not for medical diagnosis. If you have concerns about your health, please talk to a healthcare professional.",
        howToPlay: "How to Play",
        instructionsText: "Choose a category and type as many different words as you can in 60 seconds.",
        typeOneWord: "Type one word at a time and press Enter. Each unique word counts!",
        chooseCategory: "Choose a category:",
        category: "Category",
        wordsEntered: "Words entered",
        seconds: "seconds",
        typeWord: "Type a word...",
        add: "Add",
        alreadyUsed: "You already used that word!",
        yourWords: "Your words:",
        totalWords: "Total Words",
        yourResults: "Your Results",
        excellentFluency: "Your word engine is warmed up!",
        goodFluency: "Nice fluency!",
        okFluency: "Good effort!",
        feedbackText: "Quick word tasks like this rely on language networks in the brain and the \"executive\" systems that help you search, switch, and avoid repeats.",
        interestingFact: "Interesting fact",
        factText: "Verbal fluency tests are commonly used in psychology and neuroscience research to assess language retrieval, mental flexibility, and executive control. Your ability to generate words quickly depends on how efficiently your brain can search semantic memory and filter out repeated answers.",
        tipTitle: "Tip to improve",
        tipText: "To support this kind of thinking, reading widely, learning new words in any language, and doing creative activities (like storytelling or games) can all help keep language networks active and flexible.",
        playAgain: "Play Again",
        backToGames: "Back",
        categories: {
          animals: "Animals",
          foods: "Foods",
          roundThings: "Things that are round",
          kitchenThings: "Things you can find in a kitchen",
          wearableThings: "Things you can wear",
          vehicles: "Vehicles",
          sportsGames: "Sports or games",
          emotions: "Emotions/feelings",
          jobs: "Jobs or professions",
          cityPlaces: "Places in a city",
          countries: "Countries",
          weather: "Weather",
        },
      },
      memoryGrid: {
        title: "Memory Grid",
        desc: "Remember and recall visual patterns",
        duration: "1-3 mins",
        disclaimer: "Disclaimer: This game is for education and fun, not for medical diagnosis. If you have concerns about your health, please talk to a healthcare professional.",
        howToPlay: "How to Play",
        instructionsText: "Watch the pattern of lit-up squares, then click the same squares from memory.",
        eachLevel: "Each level adds one more square. The grid may grow larger as you progress!",
        level: "Level",
        watchCarefully: "Watch carefully...",
        selectSquares: "Select {count} squares",
        selected: "Selected",
        correct: "Correct!",
        yourResults: "Your Results",
        highestLevel: "Highest Level",
        largestPattern: "Largest Pattern",
        excellentMemory: "Outstanding spatial memory!",
        goodMemory: "Great performance!",
        okMemory: "Good start!",
        reachedLevel: "You reached level {level} – that's {pattern} squares to remember!",
        interestingFact: "Interesting fact",
        factText: "This game taps into visuospatial working memory – your ability to hold locations and patterns in mind for a few seconds. It's similar to remembering where you left objects in a room or navigating in a new place. Research shows that visuospatial memory involves networks in the parietal and frontal lobes of the brain.",
        tipTitle: "Tip to improve",
        tipText: "To support visuospatial memory in daily life, try using both visual and verbal cues (like describing routes aloud), reduce multitasking when you need to remember where things are, and keep clutter low in important spaces. Regular practice with spatial tasks and getting enough quality sleep also helps.",
        playAgain: "Play Again",
        backToGames: "Back",
      },
      quickReflex: {
        title: "Quick Reflex",
        desc: "Test your visual reaction speed",
        duration: "Coming soon",
      },
      focusTap: {
        title: "Focus Tap",
        desc: "Tap for targets, hold back for traps.",
        duration: "1 min",
        disclaimer: "Disclaimer: This game is for education and fun, not for medical diagnosis. If you have concerns about your health, please talk to a healthcare professional.",
        tapForTarget: "Tap for the target shape and ignore everything else.",
        roundLength: "Round length: 60 seconds",
        target: "TARGET",
        traps: "TRAPS (ignore)",
        greenCircle: "Green Circle",
        otherShapes: "Other shapes/colors",
        howToPlay: "How to Play",
        shapesAppear: "Shapes will appear one by one in the center",
        tapOrClick: "Tap/click or press spacebar ONLY for the green circle",
        doNothing: "Do nothing for all other shapes",
        paceSpeedsUp: "The pace speeds up as you go!",
        startGame: "Start Game",
        getReady: "Get ready...",
        hits: "Hits",
        falseAlarms: "False alarms",
        tapHint: "Tap the shape area or press spacebar for green circles only",
        yourResults: "Your Results",
        roundComplete: "Round complete!",
        accuracy: "Accuracy",
        focusTapScore: "Focus Tap Score",
        avgRT: "Avg RT",
        excellentFocus: "Excellent focus and impulse control! 🎯",
        goodFocus: "Good job staying focused! 👍",
        challengingFocus: "Challenging, right? The traps can be tricky to resist!",
        whatThisTrains: "What this trains",
        trainsText: "This is a \"Go/No-Go\" task. It trains response inhibition – your ability to stop an automatic response when you need to. This skill is crucial for impulse control, decision-making, and self-regulation in everyday life.",
        interestingFact: "Interesting fact",
        factText: "Response inhibition is linked to the right prefrontal cortex. Studies show that even small improvements in inhibitory control can help with focus, emotional regulation, and resisting distractions.",
        tipTitle: "Tips to improve",
        tipText: "Practice slowing down before acting in daily life. Pausing for even a second before reacting can strengthen your brain's \"stop\" circuitry. Mindfulness exercises, adequate sleep, and avoiding decision fatigue also support better impulse control.",
        playAgain: "Play Again",
        backToGames: "Back",
      },
      mathMinute: {
        title: "Math Minute",
        desc: "One minute of fast math: add, subtract, multiply, divide.",
        duration: "1 min",
        correctLabel: "correct",
      },
      memoryCard: {
        title: "Memory Cards",
        desc: "Classic matching game — find all the pairs!",
        duration: "2-5 mins",
        attemptsLabel: "attempts",
      },
      numberSequence: {
        title: "Number Path",
        desc: "Click numbers 1-25 in order as fast as you can.",
        duration: "1-2 mins",
      },
      targetShift: {
        title: "Target Shift",
        desc: "Your brain recalibrates movement when feedback changes.",
        duration: "2-4 mins",
      },
      changeBlindness: {
        title: "Change Blindness",
        desc: "Spot the one thing that changed after the flash.",
        duration: "1-2 mins",
      },
      
      // Extras
      extrasTitle: "Extras",
      visitCafe: "Visit Café",
      takePhotos: "Take Photos",
      cafe: {
        title: "OMG Café",
        desc: "Order a drink, memorise words while it brews, and see how many you can recall.",
        duration: "5-10 mins",
      },
      photoBooth: {
        title: "Photo Booth",
        desc: "A tool to capture moments. Take 4 quick snapshots and get a photo strip to keep.",
        duration: "Just for fun",
      },
    },
    
    // Exercises page
    exercises: {
      title: "Feel Better Now",
      subtitle: "Guided micro-exercises for stress relief, sharper focus, and overall brain health.",
      disclaimer: "Disclaimer: These exercises are for education and self-care, not for medical diagnosis or treatment. If you have concerns about your health, please talk to a healthcare professional.",
      backToExercises: "Back",
      backToHome: "Back to Home",
      startExercise: "Start Exercise",
      comingSoon: "Coming soon",
      moreExercisesDesc: "More exercises are on the way! Stay tuned for new practices.",
      practiceAgain: "Practice Again",
      completeExercise: "Complete Exercise",
      youCanComplete: "You can complete whenever you're ready",
      
      breathReset: {
        title: "Breath Reset",
        tagline: "Follow the square to slow your breathing and calm your mind.",
        duration: "3 minutes",
        description: "A guided 3-minute breathing practice where you follow a moving square: breathe in, hold, breathe out, hold.",
        explanation: "This simple \"box breathing\" pattern is used in many clinical and performance settings to help calm the nervous system. You'll follow a moving square on the screen. Each side of the square guides one part of the breath: inhale, hold, exhale, hold.",
        safetyNote: "If at any point you feel dizzy or uncomfortable, stop the exercise and return to your natural breathing.",
        whatTrainsTitle: "What this trains:",
        whatTrains1: "Gently slows breathing and heart rate",
        whatTrains2: "Helps shift the body towards a \"rest-and-digest\" state",
        whatTrains3: "Trains awareness of bodily sensations and breath",
        inhale: "Inhale",
        hold: "Hold",
        exhale: "Exhale",
        paused: "Paused",
        pause: "Pause",
        resume: "Resume",
        reset: "Reset",
        instructions: "Follow the moving edge with your eyes. Breathe in as the square tells you to \"Inhale\", hold when it says \"Hold\", breathe out on \"Exhale\", and hold again. Try to keep your breathing smooth and gentle, not forced.",
        nicelyDone: "Nicely done",
        completedMsg: "You just gave your nervous system a short reset.",
        noticeMsg: "Notice how your body feels now – any shifts in tension, warmth, or calm?",
        cyclesCompleted: "You completed {count} full breathing cycles",
      },
      awarenessCheckIn: {
        title: "Awareness Check-In",
        subtitle: "Mind, Body, Mood",
        tagline: "Take 2 minutes to notice what's happening inside, without changing anything.",
        duration: "2 minutes",
        description: "A 2-minute check-in to notice what's happening in your body, mind, and mood – without trying to fix anything.",
        explanation: "Awareness is the first step to gentle change. This exercise helps you take a small \"snapshot\" of your inner world – how your body feels, what your mind is doing, and what your overall mood is like right now.",
        whatTrainsTitle: "What this trains:",
        whatTrains1: "Emotional awareness",
        whatTrains2: "Interoception (sensing your body from the inside)",
        whatTrains3: "Self-regulation",
        startCheckIn: "Start Check-In",
        body: "Body",
        bodyScan: "Scan your body from head to toes. Notice any tension, restlessness, warmth, or ease – without trying to change it.",
        overallTension: "Overall body tension",
        tensionHint: "1 = very tense, 10 = very relaxed",
        veryTense: "Very tense",
        inBetween: "In between",
        veryRelaxed: "Very relaxed",
        whereTension: "Where is the tension? (select all that apply)",
        tensionAreas: {
          headJaw: "Head / jaw",
          neck: "Neck",
          shoulders: "Shoulders",
          chest: "Chest",
          stomach: "Stomach / abdomen",
          upperBack: "Upper back",
          lowerBack: "Lower back",
          armsHands: "Arms / hands",
          legsFeet: "Legs / feet",
          wholeBody: "Whole body",
          notSure: "Not sure",
        },
        mind: "Mind",
        mindNotice: "Notice where your mind is spending most of its time right now. Is it mostly in the past, the present, or the future?",
        past: "Past",
        present: "Present",
        future: "Future",
        noRightAnswer: "There's no right answer here – just notice where your mind is hanging out.",
        thoughtsLabel: "Any thoughts you'd like to note? (optional)",
        thoughtsPlaceholder: "What's on your mind right now...",
        mood: "Mood",
        moodQuestion: "If your mood right now were a colour and a number, what would they be?",
        chooseColor: "Choose a colour that fits your mood right now",
        selected: "Selected",
        overallMood: "Overall mood",
        veryLow: "Very low",
        veryHigh: "Very high",
        moodDescLabel: "Describe your mood in words (optional)",
        moodDescPlaceholder: "How would you describe how you feel...",
        completeCheckIn: "Complete Check-In",
        addPhoto: "Add a photo (optional)",
        addPhotoDesc: "Capture a moment – yourself or your surroundings",
        takePhoto: "Take Photo",
        removePhoto: "Remove",
        snapshotTitle: "Your snapshot right now",
        notSpecified: "Not specified",
        notSelected: "Not selected",
        mostlyIn: "Mostly in the",
        notYetSelected: "Not yet selected.",
        snapshotNote1: "This is just a snapshot – your inner state changes all the time, even within a single day.",
        snapshotNote2: "Checking in like this helps your brain notice patterns over time, without judgment.",
        completeTitle: "Your Awareness Check-In",
        completeMsg: "There's nothing you have to change right now – simply noticing is already a powerful step for your brain and mental health.",
        checkInAgain: "Check In Again",
        thoughts: "Thoughts",
        moodDescription: "Mood in words",
        moodColors: {
          grey: "Soft grey",
          blue: "Calm blue",
          green: "Fresh green",
          yellow: "Warm yellow",
          orange: "Bright orange",
          red: "Deep red",
          purple: "Gentle purple",
        },
      },
      fiveSenseGrounding: {
        title: "Five-Sense Grounding",
        tagline: "Come back to the present using sight, sound, touch, smell, and taste.",
        duration: "3 minutes",
        description: "A short grounding exercise that uses your five senses to bring you back into the present moment.",
        explanation: "When we're stressed or overwhelmed, the mind can spin into worries about the past or future. This exercise gently brings attention back to the here-and-now by guiding you through what you can see, feel, hear, smell, and taste around you.",
        whatTrainsTitle: "What this trains:",
        whatTrains1: "Grounding in the present moment",
        whatTrains2: "Stress reduction",
        whatTrains3: "Sensory awareness",
        pattern: "5 – 4 – 3 – 2 – 1",
        see5: "5 things you can see",
        see5Desc: "Look around and silently name five things you can see. They can be simple: a mug, a shadow, a book, a colour, your own hands.",
        feel4: "4 things you can feel",
        feel4Desc: "Notice four things you can feel through touch – for example, your feet on the ground, your clothes on your skin, air on your face, or the texture of an object near you.",
        hear3: "3 things you can hear",
        hear3Desc: "Listen carefully for three sounds – near or far. It could be traffic, a fridge hum, birds, a computer fan, your own breathing.",
        smell2: "2 things you can smell",
        smell2Desc: "Notice two smells. If you can't smell much right now, that's okay – you can move closer to something with a scent (soap, coffee, hand cream) or simply notice the \"neutral\" smell of the room.",
        taste1: "1 thing you can taste",
        taste1Desc: "Notice one thing you can taste. It might be the aftertaste of a drink, your toothpaste, or just the natural taste in your mouth. If that's hard, you can also imagine a taste you enjoy.",
        grounded: "You're grounded",
        groundedMsg: "You've just anchored your attention in the present moment using your senses.",
        groundedNotice: "Notice whether your body or mind feel even slightly different compared to when you started.",
      },
      panicSOS: {
        title: "Panic SOS – Riding the Wave",
        tagline: "Step-by-step guidance to help you ride out a wave of high anxiety.",
        duration: "3–5 minutes",
        description: "A short, step-by-step guide to help you ride out a wave of high anxiety or mild panic, until it passes.",
        explanation: "Panic and high anxiety can feel very intense and frightening, but the sensations themselves are usually not dangerous. This exercise helps you stay with the wave and let it rise, peak, and fall.",
        safetyTitle: "Important Safety Note",
        safetyText: "This exercise is for mild to moderate panic or high anxiety. If you have chest pain, trouble breathing, feel faint, think you might be having a heart attack, or have thoughts of harming yourself, please seek urgent medical help immediately.",
        step1: "Name the wave",
        step1Desc: "Briefly name what's happening. You don't need details – just a simple phrase.",
        step1Placeholder: "Panic on the train, Anxiety before a meeting...",
        intensityNow: "How intense does it feel right now?",
        intensityScale: "0 = very low, 10 = extremely high",
        step2: "Plant your body",
        step2Desc: "Help your nervous system feel where your body is supported right now.",
        groundingOptions: {
          feet: "Both feet flat on the floor",
          back: "Back supported by chair or wall",
          hands: "Hands gently pressing together",
          ground: "Noticing contact with ground or seat",
        },
        chooseWhatPossible: "You don't have to do all of them – just choose what is possible for you right now.",
        step3: "Soften the breath",
        step3Desc: "Let your breath slow down gently, without forcing it.",
        inhaleFor: "Inhale through your nose for about 4 seconds.",
        exhaleFor: "Exhale through your mouth for about 6 seconds, like you're slowly blowing out a candle.",
        breathsCompleted: "Breaths completed",
        of: "of",
        start: "Start",
        breath: "+1 Breath",
        step4: "Look around",
        step4Desc: "Let your eyes move around the space you're in. Gently name what's actually here, right now.",
        thingsISee: "3 things I can see",
        thingsIFeel: "2 things I can feel (touch)",
        thingsIHear: "1 sound I can hear",
        step5: "Re-check the wave",
        step5Desc: "Panic waves rise and fall. Let's see how this one is doing now.",
        intensityBefore: "Before",
        intensityAfter: "Now",
        waveDecreased: "Even a small change is a shift. Well done for staying with the wave.",
        waveSame: "If nothing has changed, that's okay too. The wave will still pass.",
        closingMsg: "You've just stayed with a difficult wave instead of fighting it.",
        closingNote: "If high anxiety or panic keeps coming back, consider talking to a mental health professional for more support.",
      },
      nightTimeWorry: {
        title: "Night-Time Worry Parking",
        tagline: "Move racing thoughts out of your head and \"park\" them for tomorrow.",
        duration: "5 minutes",
        description: "A short exercise to get worries out of your head and onto \"parked\" notes for tomorrow.",
        explanation: "At night, the brain often starts replaying problems on a loop. Writing worries down and sorting them can help your mind let go for sleep – knowing that tomorrow-you can pick them up again.",
        step1: "Write your worries down",
        step1Desc: "In a few short lines, write what your mind keeps coming back to. One worry per line is enough.",
        step1Placeholder: "Work deadline\nArgument with friend\nMoney\nHealth check next week...",
        next: "Next",
        step2: "Sort your worries",
        step2Desc: "For each worry, ask: \"Is there a concrete action I can take tomorrow?\" If yes, we'll note a tiny next step. If not, we'll mark it as a worry you're not able to act on right now.",
        worryOf: "Worry {current} of {total}",
        canActTomorrow: "Can act on tomorrow",
        cantActNow: "Can't act on right now",
        smallestNextStep: "Smallest next step for tomorrow:",
        nextStepPlaceholder: "Send email to..., Check account, Make a list...",
        previous: "Previous",
        finish: "Finish",
        step3: "Your worries are parked",
        step3Desc: "Here's a snapshot of what you've parked for tomorrow. We don't store this data on a server, so if you want to keep it, please take a screenshot or copy the text before you close this page.",
        actionsForTomorrow: "Actions for tomorrow",
        letGoTonight: "Things to gently let go of for tonight:",
        nextStepLabel: "Next step",
        copyAll: "Copy all to clipboard",
        copied: "Copied to clipboard!",
        copyFailed: "Could not copy to clipboard",
        closingMsg: "You've given your brain a signal:",
        closingMsg2: "\"These worries are written down. I can return to them tomorrow.\"",
        closingNote: "Your only job now is to rest. If sleep problems or anxiety are very frequent or severe, consider talking to a health professional for extra support.",
      },
      cognitiveShuffle: {
        title: "Cognitive Shuffle",
        tagline: "A gentle mental reset when thoughts won't stop looping.",
        duration: "3–5 minutes",
      },
      eisenhowerMatrix: {
        title: "Eisenhower Matrix",
        tagline: "Prioritize tasks by urgency and importance.",
        duration: "5–15 min",
      },
      photoBooth: {
        title: "Photo Booth",
        tagline: "A tool to capture moments for yourself.",
        duration: "Just for fun",
      },
    },
    
    // Questionnaires page
    questionnaires: {
      title: "Check-ins",
      subtitle: "Science-based self-reflection tools to check in on your mood, anxiety, sleep, and stress levels.",
      disclaimer: "Disclaimer",
      disclaimerText: "These questionnaires do not provide a diagnosis and do not replace professional medical advice. If your symptoms worry you or interfere with daily life, please talk to a doctor, psychologist, or qualified health professional.",
      backToHome: "Back to Home",
      backToQuestionnaires: "Back",
      returnToQuestionnaires: "Back",
      startQuestionnaire: "Start Questionnaire",
      estimatedTime: "Estimated time",
      comingSoon: "Coming soon",
      moreQuestionnairesDesc: "More questionnaires are on the way! Stay tuned for new self-reflection tools.",
      scienceBased: "Science-based",
      ageRestriction: "Age",
      years: "years",
      plusYears: "+ years",
      
      phq9: {
        title: "PHQ-9 – Mood Check-In",
        desc: "A brief, research-based mood questionnaire that helps you reflect on common symptoms of low mood over the last 2 weeks.",
        time: "2–5 minutes",
      },
      gad7: {
        title: "GAD-7 – Anxiety Check-In",
        desc: "A short, scientific questionnaire about worry and anxiety over the last 2 weeks.",
        time: "2–4 minutes",
      },
      pss10: {
        title: "PSS-10 – Stress Check-In",
        desc: "A widely used 10-item questionnaire about how stressful and overwhelming life has felt over the last month.",
        time: "3–5 minutes",
      },
      maas: {
        title: "MAAS – Mindfulness Check-In",
        desc: "A brief questionnaire about how often you're present and attentive in everyday life.",
        time: "3–5 minutes",
      },
      psqi: {
        title: "PSQI – Sleep Quality Check-In",
        desc: "A research-based questionnaire about your sleep quality over the last month.",
        time: "5–7 minutes",
      },
      ucla: {
        title: "UCLA Loneliness Scale – Connection Check-In",
        desc: "A widely used questionnaire about feelings of connection and loneliness.",
        time: "5–7 minutes",
      },
      cbi: {
        title: "CBI – Burnout Check-In",
        desc: "A 19-item questionnaire about personal, work-related and client-related burnout.",
        time: "5–7 minutes",
      },
      criq: {
        title: "CRIq – Brain Reserve Check-In",
        desc: "A questionnaire about your lifelong learning, work and hobbies that build 'brain reserve'. Especially useful if you're thinking about memory and ageing.",
        time: "5–7 minutes",
      },
      cllp: {
        title: "CLLP – Cognitive Load & Longevity Profile",
        desc: "A comprehensive, science-grounded profile of your cognitive health — covering sleep, stress, attention, memory, and more. Personalised to your answers. Actionable from day one.",
        time: "25 minutes",
      },
    },
    
    // Common questionnaire text
    questionnaire: {
      questionOf: "Question {current} of {total}",
      back: "Back",
      next: "Next",
      seeResults: "See my results",
      canStop: "You can stop at any time. Your answers are not saved permanently.",
      outOf: "out of {max} points",
      outOfAvg: "out of {max} (average score)",
      exploreNext: "Explore next steps with Oh Mind Guide",
      
      // PHQ-9
      phq9: {
        title: "PHQ-9 – Mood Check-In",
        intro: "Over the last 2 weeks, how often have you been bothered by the following problems?",
        resultTitle: "Your Mood Check-In Result (PHQ-9)",
        items: [
          "Little interest or pleasure in doing things",
          "Feeling down, depressed, or hopeless",
          "Trouble falling or staying asleep, or sleeping too much",
          "Feeling tired or having little energy",
          "Poor appetite or overeating",
          "Feeling bad about yourself - or that you are a failure or have let yourself or your family down",
          "Trouble concentrating on things, such as reading the newspaper or watching television",
          "Moving or speaking so slowly that other people could have noticed. Or the opposite - being so fidgety or restless that you have been moving around a lot more than usual",
          "Thoughts that you would be better off dead, or of hurting yourself",
        ],
        categories: {
          low: "Low range",
          mild: "Mild range",
          moderate: "Moderate range",
          high: "High range",
          veryHigh: "Very high range",
        },
        descriptions: {
          low: "Your answers are in the lower range for mood symptoms on this questionnaire.",
          mild: "You reported some mood-related symptoms.",
          moderate: "You reported a number of mood symptoms that fall in the moderate range on this questionnaire.",
          high: "Your score falls in the high range for mood symptoms on this questionnaire.",
          veryHigh: "Your score is in the very high range for mood symptoms on this questionnaire.",
        },
        recommendations: {
          low: [
            "Everyone has ups and downs, but right now you're not reporting many of the typical symptoms this scale asks about.",
            "Keep maintaining healthy routines like regular sleep, physical activity, and social connections.",
            "Explore our brain health articles and gentle games to support your wellbeing.",
          ],
          mild: [
            "This does not automatically mean you have depression, but it's a signal to pay attention to how you feel.",
            "Consider gentle self-care: maintain a regular sleep schedule, engage in physical activity, and plan small enjoyable activities.",
            "Talking with a trusted person can help.",
            "If these feelings have lasted longer than 2 weeks or are getting worse, consider chatting with a health professional.",
          ],
          moderate: [
            "This can be a sign that your emotional load is quite heavy at the moment.",
            "We strongly encourage you to talk to a doctor or mental health professional soon, especially if symptoms have lasted several weeks or affect work, study, or relationships.",
            "Seeking help is a sign of taking brain health seriously, not a weakness.",
            "Continue with self-care basics: sleep, nutrition, gentle movement, and connection with others.",
          ],
          high: [
            "This suggests you're experiencing significant emotional distress.",
            "Please talk to a doctor or mental health professional as soon as possible.",
            "If symptoms include hopelessness, very low motivation, or thoughts of self-harm, seeking help is urgent.",
            "You deserve support, and effective treatments are available.",
          ],
          veryHigh: [
            "This indicates severe symptoms that need professional attention.",
            "Please contact a doctor, therapist, or mental health crisis service immediately.",
            "If you're having thoughts of self-harm, please reach out to emergency services or a crisis helpline right away.",
            "Recovery is possible with the right support and treatment.",
          ],
        },
        suicideRisk: {
          title: "Urgent: Please Seek Help",
          text: "Because you indicated some thoughts about self-harm or not wanting to be here, it's very important to talk to someone as soon as possible.",
          ifAtRisk: "If you feel at risk of hurting yourself, please contact:",
          contacts: [
            "Emergency services (112 or 911)",
            "A local crisis or suicide helpline",
            "Your doctor or mental health provider",
            "A trusted friend or family member",
          ],
        },
        nextSteps: {
          articlesTitle: "Read Articles",
          articlesDesc: "Explore science-based guides about mood, sleep, and brain resilience",
          gamesTitle: "Try Brain Games",
          gamesDesc: "Gently train your brain with playful, science-based exercises",
        },
      },
      
      // GAD-7
      gad7: {
        title: "GAD-7 – Anxiety Check-In",
        intro: "Over the last 2 weeks, how often have you been bothered by the following problems?",
        resultTitle: "Your Anxiety Check-In Result (GAD-7)",
        items: [
          "Feeling nervous, anxious, or on edge",
          "Not being able to stop or control worrying",
          "Worrying too much about different things",
          "Trouble relaxing",
          "Being so restless that it is hard to sit still",
          "Becoming easily annoyed or irritable",
          "Feeling afraid, as if something awful might happen",
        ],
        categories: {
          low: "Low range",
          mild: "Mild range",
          moderate: "Moderate range",
          high: "High range",
        },
        descriptions: {
          low: "Your answers suggest a low level of anxiety symptoms on this questionnaire.",
          mild: "You reported some anxiety or worry.",
          moderate: "Your answers are in the moderate range for anxiety symptoms on this questionnaire.",
          high: "Your score falls in the high range for anxiety symptoms on this questionnaire.",
        },
        recommendations: {
          low: [
            "You're not reporting many anxiety-related symptoms at the moment.",
            "Continue with healthy habits that support your wellbeing.",
            "Explore our brain resilience and stress-prevention content to maintain your mental fitness.",
          ],
          mild: [
            "Many people experience this during stressful times, but it's useful to keep an eye on it.",
            "Try simple breathing or grounding exercises when you notice anxiety rising.",
            "Read our articles on understanding worry and the stress response.",
            "If anxiety has been persistent or is distressing, consider talking with a health professional.",
          ],
          moderate: [
            "This can mean anxiety is taking up a lot of mental space.",
            "We encourage you to talk with a health professional about what you're experiencing.",
            "Building a support network with friends, family, or peer groups can help.",
            "Explore relaxation techniques and nervous system calming practices.",
          ],
          high: [
            "This suggests you're experiencing significant anxiety that may be interfering with daily life.",
            "Please talk to a doctor or mental health professional soon.",
            "Anxiety is very treatable, and effective therapies and support are available.",
            "In the meantime, practice gentle self-care and reach out to trusted people.",
          ],
        },
        nextSteps: {
          articlesTitle: "Read Articles",
          articlesDesc: "Learn about anxiety, stress response, and calming techniques",
          gamesTitle: "Try Brain Games",
          gamesDesc: "Practice attention and mindfulness with gentle exercises",
        },
      },
      
      // PSS-10
      pss10: {
        title: "PSS-10 – Stress Check-In",
        intro: "In the last month, how often have you...",
        resultTitle: "Your Stress Check-In Result (PSS-10)",
        items: [
          "Been upset because of something that happened unexpectedly?",
          "Felt that you were unable to control the important things in your life?",
          "Felt nervous and stressed?",
          "Felt confident about your ability to handle your personal problems?",
          "Felt that things were going your way?",
          "Found that you could not cope with all the things that you had to do?",
          "Been able to control irritations in your life?",
          "Felt that you were on top of things?",
          "Been angered because of things that were outside of your control?",
          "Felt difficulties were piling up so high that you could not overcome them?",
        ],
        categories: {
          low: "Low perceived stress",
          moderate: "Moderate perceived stress",
          high: "High perceived stress",
        },
        descriptions: {
          low: "Your score is in the lower range for perceived stress over the last month.",
          moderate: "Your score falls in the moderate range for perceived stress.",
          high: "Your score is in the higher range of perceived stress on this scale.",
        },
        recommendations: {
          low: [
            "You're managing life's demands relatively well at the moment.",
            "Continue maintaining routines and resilience habits.",
            "Explore our content about brain-protective lifestyle: sleep, movement, and cognitive engagement.",
          ],
          moderate: [
            "This is common when life is busy or demanding.",
            "Consider small, concrete stress-management steps: planning breaks, setting boundaries, and short relaxation practices.",
            "Notice if stress is starting to affect sleep, mood, or concentration.",
            "If stress is persistent or overwhelming, consider talking with a professional.",
          ],
          high: [
            "This suggests that, recently, life has felt quite overwhelming.",
            "We strongly encourage you to consider professional support (doctor, therapist, counselor).",
            "Try to reduce your load where possible: ask for help, delegate tasks, and set priorities.",
            "Practice self-compassion and remember that high stress is a signal to take action.",
            "Explore our resources about stress, burnout, and recovery.",
          ],
        },
        disclaimerNote: "It measures your perception of stress, which is subjective and can vary from day to day.",
        nextSteps: {
          articlesTitle: "Read Articles",
          articlesDesc: "Learn about stress, brain resilience, and recovery strategies",
          gamesTitle: "Try Brain Games",
          gamesDesc: "Small steps for a calmer mind with playful exercises",
        },
      },
      
      // MAAS
      maas: {
        title: "MAAS – Mindfulness Check-In",
        intro: "Please indicate how frequently or infrequently you currently have each experience.",
        resultTitle: "Your Mindfulness Check-In (MAAS)",
        items: [
          "I could be experiencing some emotion and not be conscious of it until sometime later.",
          "I break or spill things because of carelessness, not paying attention, or thinking of something else.",
          "I find it difficult to stay focused on what's happening in the present.",
          "I tend to walk quickly to get where I'm going without paying attention to what I experience along the way.",
          "I tend not to notice feelings of physical tension or discomfort until they really grab my attention.",
          "I forget a person's name almost as soon as I've been told it for the first time.",
          "It seems I am 'running on automatic' without much awareness of what I'm doing.",
          "I rush through activities without being really attentive to them.",
          "I get so focused on the goal I want to achieve that I lose touch with what I am doing right now to get there.",
          "I do jobs or tasks automatically, without being aware of what I'm doing.",
          "I find myself listening to someone with one ear, doing something else at the same time.",
          "I drive places on 'automatic pilot' and then wonder why I went there.",
          "I find myself preoccupied with the future or the past.",
          "I find myself doing things without paying attention.",
          "I snack without being aware that I'm eating.",
        ],
        categories: {
          low: "Lower everyday mindfulness",
          moderate: "Moderate everyday mindfulness",
          high: "Higher everyday mindfulness",
        },
        descriptions: {
          low: "Your answers suggest that you often find yourself on 'autopilot' or distracted from the present moment.",
          moderate: "Your answers are in the middle range for everyday mindfulness. You have some moments of presence and some moments of automatic pilot – which is very common.",
          high: "Your answers suggest you are often aware of what's happening in the present moment. This level of mindfulness can support emotional balance and brain health.",
        },
        recommendations: {
          low: [
            "This is very common in our busy lives, and there's no judgment – it's simply information.",
            "Try short, accessible mindfulness practices (1–3 minutes of mindful breathing or noticing sensations).",
            "Blend small moments of awareness into daily routines – like noticing the feeling of water while washing your hands.",
            "Explore our mindfulness articles and grounding exercises.",
          ],
          moderate: [
            "Consider building a bit more intentional awareness in your daily life.",
            "Try a simple weekly challenge: one mindful activity per day (mindful walking, eating, or listening).",
            "Read our articles about how mindfulness supports brain resilience, stress management, and attention.",
            "Practice being curious rather than critical about when your mind wanders.",
          ],
          high: [
            "Keep maintaining these habits – they're protecting your wellbeing.",
            "Consider exploring more advanced or playful mindfulness exercises (mindful listening, mindful communication).",
            "Share your practice with others who might benefit.",
            "Try our attention games that train present-moment focus.",
          ],
        },
        disclaimerNote: "This result is for reflection and personal development, not a clinical evaluation or diagnosis. The MAAS measures present-moment awareness in everyday life. It's one tool among many for understanding your experience.",
        nextStepsTitle: "Tiny steps for a calmer, more present mind",
        nextSteps: {
          articlesTitle: "Mindfulness Articles",
          articlesDesc: "Learn about attention, brain resilience, and present-moment awareness",
          gamesTitle: "Attention Games",
          gamesDesc: "Train your focus and present-moment awareness with playful exercises",
        },
      },
      
      // PSQI
      psqi: {
        title: "PSQI – Sleep Quality Check-In",
        intro: "The following questions relate to your usual sleep habits during the past month only.",
        resultTitle: "Your Sleep Check-In (PSQI)",
        questions: {
          bedtime: "During the past month, when have you usually gone to bed at night?",
          fallAsleep: "During the past month, how long (in minutes) has it usually taken you to fall asleep each night?",
          wakeTime: "During the past month, when have you usually gotten up in the morning?",
          sleepHours: "During the past month, how many hours of actual sleep did you get at night? (This may be different than the number of hours you spent in bed.)",
          qualityOverall: "During the past month, how would you rate your sleep quality overall?",
          troubleSleepWithin30: "During the past month, how often have you had trouble sleeping because you cannot get to sleep within 30 minutes?",
          wakeUpMiddle: "During the past month, how often have you had trouble sleeping because you wake up in the middle of the night or early morning?",
          medication: "During the past month, how often have you taken medicine to help you sleep (prescribed or over the counter)?",
          troubleStayingAwake: "During the past month, how often have you had trouble staying awake while driving, eating meals, or engaging in social activity?",
          enthusiasm: "During the past month, how much of a problem has it been for you to keep up enough enthusiasm to get things done?",
        },
        qualityOptions: {
          veryGood: "Very good",
          fairlyGood: "Fairly good",
          fairlyBad: "Fairly bad",
          veryBad: "Very bad",
        },
        frequencyOptions: {
          notDuringPastMonth: "Not during the past month",
          lessThanOnce: "Less than once a week",
          onceOrTwice: "Once or twice a week",
          threeOrMore: "Three or more times a week",
        },
        problemOptions: {
          noProblem: "No problem at all",
          slightProblem: "Only a very slight problem",
          somewhatProblem: "Somewhat of a problem",
          bigProblem: "A very big problem",
        },
        enterNumber: "Enter number",
        categories: {
          good: "Good sleep quality range",
          some: "Some sleep difficulties",
          significant: "Significant sleep difficulties",
        },
        descriptions: {
          good: "Your score is in the range usually associated with better sleep quality. You might still have occasional bad nights – that's normal – but overall this questionnaire doesn't pick up major sleep difficulties.",
          some: "Your score suggests you've been experiencing some sleep difficulties recently. This might include longer time to fall asleep, night awakenings, or daytime tiredness.",
          significant: "Your score is in a range where many people find that sleep problems noticeably affect their daytime energy, mood, or concentration.",
        },
        recommendations: {
          good: [
            "Maintain healthy sleep habits: consistent schedule, light exposure during the day, and movement.",
            "Keep your bedroom cool, dark, and quiet.",
            "Explore our sleep content to protect your sleep health long-term.",
            "Remember that stress or life changes can temporarily affect sleep.",
          ],
          some: [
            "Try simple sleep hygiene changes: a wind-down routine, reducing screens before bed, regular wake time.",
            "Keep a sleep diary for a week to identify patterns.",
            "Explore our relaxation exercises before bed.",
            "If sleep problems persist or worsen, consider talking to a healthcare professional.",
            "Check if stress, worry, or physical discomfort are affecting your sleep.",
          ],
          significant: [
            "We strongly encourage you to discuss your sleep concerns with a doctor or sleep specialist.",
            "Sleep problems are common and treatable – you don't have to accept poor sleep as 'normal'.",
            "Explore cognitive behavioral therapy for insomnia (CBT-I), which is highly effective.",
            "Read our articles on insomnia, circadian rhythm, and stress–sleep interactions.",
            "Meanwhile, maintain a consistent wake time and avoid long daytime naps.",
          ],
        },
        disclaimerNote: "It measures your subjective sleep quality, which is important but doesn't replace a clinical sleep assessment. If sleep problems are persistent or severe, or if you suspect a sleep disorder (like sleep apnea), please talk to a healthcare professional.",
        nextStepsTitle: "Small experiments to gently improve your sleep",
        nextSteps: {
          articlesTitle: "Sleep Articles",
          articlesDesc: "Learn about sleep hygiene, circadian rhythm, and relaxation techniques",
          gamesTitle: "Calming Exercises",
          gamesDesc: "Try gentle activities for daytime functioning and nighttime relaxation",
        },
      },
      
      // UCLA
      ucla: {
        title: "UCLA Loneliness Scale – Connection Check-In",
        intro: "Please indicate how often you feel the way described in each statement.",
        resultTitle: "Your Connection Check-In (UCLA Loneliness Scale)",
        items: [
          "How often do you feel that you are 'in tune' with the people around you?",
          "How often do you feel that you lack companionship?",
          "How often do you feel that there is no one you can turn to?",
          "How often do you feel alone?",
          "How often do you feel part of a group of friends?",
          "How often do you feel that you have a lot in common with the people around you?",
          "How often do you feel that you are no longer close to anyone?",
          "How often do you feel that your interests and ideas are not shared by those around you?",
          "How often do you feel outgoing and friendly?",
          "How often do you feel close to people?",
          "How often do you feel left out?",
          "How often do you feel that your relationships with others are not meaningful?",
          "How often do you feel that no one really knows you well?",
          "How often do you feel isolated from others?",
          "How often do you feel you can find companionship when you want it?",
          "How often do you feel that there are people who really understand you?",
          "How often do you feel shy?",
          "How often do you feel that people are around you but not with you?",
          "How often do you feel that there are people you can talk to?",
          "How often do you feel that there are people you can turn to?",
        ],
        categories: {
          low: "Lower range of loneliness feelings",
          moderate: "Moderate loneliness feelings",
          high: "Higher loneliness feelings",
        },
        descriptions: {
          low: "Your answers suggest that, overall, you usually feel reasonably connected to others. Everyone can feel lonely at times, and that's completely normal.",
          moderate: "Your answers suggest you've been experiencing some feelings of loneliness or disconnection. This is very common, especially during transitions, high stress, or big life changes.",
          high: "Your answers suggest loneliness has been quite present in your life recently. Feeling isolated can be hard emotionally and can also affect brain and body health over time.",
        },
        recommendations: {
          low: [
            "Maintain meaningful relationships and community activities that bring you joy.",
            "Stay curious about deepening existing connections rather than only seeking new ones.",
            "Explore our content about social engagement and brain health.",
            "Remember that quality of connection matters more than quantity.",
          ],
          moderate: [
            "Consider small, realistic steps: reaching out to one person, joining a hobby group, or exploring online communities that feel safe.",
            "Remember that loneliness is not a character flaw – it's a signal, like hunger or thirst.",
            "Read our articles about building social connection and overcoming social withdrawal.",
            "If loneliness is persistent and painful, consider speaking with a therapist or counselor.",
            "Volunteer work or structured social activities can provide regular connection.",
          ],
          high: [
            "We strongly encourage you to seek support: trusted friends/family, support groups, or a mental health professional.",
            "Therapy (especially approaches focused on relationships and social anxiety) can be very helpful.",
            "Consider structured activities that bring regular connection: classes, groups, volunteering.",
            "Small steps count: even one meaningful interaction per week is progress.",
            "Explore our resources about connection, social health, and loneliness.",
            "Remember that these feelings are valid and change is possible with support.",
          ],
        },
        disclaimerNote: "This result describes how you answered this scale; it is not a diagnosis. Loneliness is a subjective experience, and this questionnaire captures your perception at this moment in time. If loneliness feels overwhelming or is affecting your daily life, talking with a health professional can help.",
        nextStepsTitle: "Ways to feel a bit less alone over time",
        nextSteps: {
          articlesTitle: "Connection Articles",
          articlesDesc: "Learn about social health, building connections, and overcoming isolation",
          gamesTitle: "Social Activities",
          gamesDesc: "Explore activities that involve connection and shared experiences",
        },
      },
      
      // Response options
      responses: {
        notAtAll: "Not at all",
        severalDays: "Several days",
        moreThanHalf: "More than half the days",
        nearlyEvery: "Nearly every day",
        
        never: "Never",
        almostNever: "Almost never",
        sometimes: "Sometimes",
        fairlyOften: "Fairly often",
        veryOften: "Very often",
        
        almostAlways: "Almost always",
        veryFrequently: "Very frequently",
        somewhatFrequently: "Somewhat frequently",
        somewhatInfrequently: "Somewhat infrequently",
        veryInfrequently: "Very infrequently",
        almostNeverMaas: "Almost never",
        
        rarely: "Rarely",
        often: "Often",
      },
      
      // Common results text
      importantDisclaimer: "Important Disclaimer",
      screeningTool: "This questionnaire is a screening and reflection tool, not a diagnosis.",
      cannotTell: "It cannot tell you for sure whether you have a mental health condition.",
      ifSymptomsWorry: "If your symptoms worry you, interfere with your daily life, or if your score is in the higher ranges, consider talking to a doctor, psychologist, or other qualified health professional.",
      
      crisisResources: "Crisis Resources",
      crisisText: "If you are thinking about harming yourself, or you feel in immediate danger, please seek immediate help:",
      crisisContacts: [
        "Contact local emergency services (112 or 911)",
        "Call your local crisis or suicide helpline",
        "Go to your nearest emergency department",
      ],
      
      readArticles: "Read Articles",
      tryGames: "Try Brain Games",
    },
    
    // Photo Booth
    photoBooth: {
      title: "Retro Photo Booth",
      subtitle: "A tool to capture moments: four quick snapshots, one photo strip to keep.",
      vintageSubtitle: "Four quick snapshots, one photo strip to keep.",
      description: "Step into a tiny virtual photo booth. When you're ready, start the session, watch the countdown, and let the camera capture four moments with small pauses in between. At the end, your photos appear as a retro-style strip you can download and keep.",
      backToGames: "Back",
      boothTitle: "Retro Photo Booth",
      readyMessage: "Get ready – 4 photos in a row",
      cameraError: "Looks like we can't access your camera. Please check your browser settings or try a different device.",
      tryAgain: "Try Again",
      photoOf: "Photo {current} of {total}",
      processing: "Creating your photo strip...",
      startSession: "Start photo session",
      download: "Download photo strip",
      downloadStrip: "Download strip",
      retake: "Retake photos",
      privacyNote: "Your photos stay on your device – we don't store them.",
      privacyNotice: "We don't store your photos – they stay on your device unless you download them.",
      capturing: "Hold still, capturing...",
      printing: "Printing your photos...",
    },
    
    // Sampler page
    sampler: {
      title: "Your Unique OMG Sampler",
      subtitle: "Made just for you ✨ (shuffle anytime)",
      savedNote: "Saved on this device.",
      shuffleAll: "Shuffle All",
      open: "Open",
      shuffle: "Shuffle",
      more: "More",
      backToHome: "Back to Home",
      back: "Back",
      showMeBtn: "Show me what you've got",
      
      // Category names
      categories: {
        game: "Game",
        exercise: "Exercise",
        cafe: "OMG Café",
        questionnaire: "Questionnaire",
        article: "Article",
        photobooth: "Photo Booth",
      },
      
      // Game items
      games: {
        colour: { title: "Colour-Word Challenge", desc: "Match colours while ignoring text in this Stroop-inspired task" },
        number: { title: "Number Echo", desc: "Remember and recall sequences of numbers" },
        focustap: { title: "Focus Tap", desc: "Tap targets quickly and accurately" },
        memorygrid: { title: "Memory Grid", desc: "Remember patterns on a grid" },
        mathminute: { title: "Math Minute", desc: "Solve math problems against the clock" },
        wordsprint: { title: "Word Sprint", desc: "Type words as fast as you can" },
        reaction: { title: "Reaction Dash", desc: "Test your reflexes with quick button presses" },
        memorycard: { title: "Memory Cards", desc: "Find matching pairs in this classic game" },
        numbersequence: { title: "Number Path", desc: "Click numbers 1-25 in order" },
        targetshift: { title: "Target Shift", desc: "Your brain recalibrates movement" },
        changeblindness: { title: "Change Blindness", desc: "Spot what changed after the flash" },
      },
      
      // Exercise items
      exercises: {
        boxbreathing: { title: "Box Breathing", desc: "A calming 4-4-4-4 breathing technique" },
        awarenesscheckin: { title: "Awareness Check-In", desc: "Connect with your body and mind" },
        fivesensegrounding: { title: "5-Sense Grounding", desc: "Ground yourself using all five senses" },
        panicsos: { title: "Panic SOS", desc: "Quick relief for overwhelming moments" },
        nighttimeworry: { title: "Night-Time Worry Parking", desc: "Release worries before sleep" },
        cognitiveshuffle: { title: "Cognitive Shuffle", desc: "A gentle reset when thoughts won't stop looping" },
        eisenhowermatrix: { title: "Eisenhower Matrix", desc: "Prioritize tasks by urgency and importance" },
        ambientstudio: { title: "Soundscape Studio", desc: "Mix your perfect ambient soundscape" },
      },
      
      // Cafe
      cafe: { title: "OMG Café", desc: "Build a cozy drink & train your memory" },
      
      // Questionnaire items
      questionnaires: {
        phq9: { title: "PHQ-9 Depression Screen", desc: "Assess symptoms of depression" },
        gad7: { title: "GAD-7 Anxiety Screen", desc: "Measure anxiety levels" },
        pss10: { title: "PSS-10 Stress Scale", desc: "Evaluate your perceived stress" },
        maas: { title: "MAAS Mindfulness Scale", desc: "Measure your mindfulness awareness" },
        psqi: { title: "PSQI Sleep Quality", desc: "Assess your sleep quality" },
        ucla: { title: "UCLA Loneliness Scale", desc: "Measure feelings of loneliness" },
        cbi: { title: "CBI Burnout Check-In", desc: "Assess personal and work-related burnout" },
        criq: { title: "CRIq Brain Reserve", desc: "Explore your cognitive reserve factors" },
      },
      
      // Article items
      articles: {
        "brain-and-mind": { title: "Brain & Mind", desc: "Explore the foundations of neuroscience" },
        dementia: { title: "Dementia", desc: "Understanding memory and cognitive decline" },
        parkinsons: { title: "Parkinson's Disease", desc: "Movement, dopamine, and the brain" },
        adhd: { title: "ADHD", desc: "Attention, focus, and executive function" },
        sleep: { title: "Sleep", desc: "The science of rest and restoration" },
        autism: { title: "Autism", desc: "Neurodiversity and unique perspectives" },
        schizophrenia: { title: "Schizophrenia", desc: "Perception, reality, and the mind" },
        anxiety: { title: "Anxiety", desc: "Understanding fear and worry" },
        depression: { title: "Depression", desc: "Mood, motivation, and the brain" },
        ptsd: { title: "PTSD", desc: "Trauma, memory, and healing" },
        "feelings-emotions": { title: "Feelings & Emotions", desc: "The science behind what we feel" },
        "pain-nociception": { title: "Pain & Nociception", desc: "How we perceive and process pain" },
        "sense-organs": { title: "Sense Organs", desc: "How we perceive the world" },
        microbiota: { title: "Microbiota & the Nervous System", desc: "How gut microbes talk to the brain" },
        "coffee-tea": { title: "Coffee, Tea & the Brain", desc: "Caffeine, polyphenols, and brain health" },
      },
      
      // Photo booth
      photobooth: { title: "Photo Booth", desc: "A tool to capture moments" },
      
      // Time labels
      times: {
        "1min": "1 min",
        "1-2min": "1-2 min",
        "2-3min": "2-3 min",
        "3-5min": "3-5 min",
        "5min": "5 min",
        "5-7min": "5-7 min",
        "5-10min": "5-10 min",
        "8-10min": "8-10 min",
        "10-12min": "10-12 min",
        "12-15min": "12-15 min",
        justForFun: "Just for fun",
      },
    },
    
    // Next Steps cross-linking
    nextSteps: {
      title: "Next steps",
      openButton: "Open",
      categoryLabels: {
        article: "Article",
        game: "Game",
        exercise: "Exercise",
        questionnaire: "Questionnaire",
      },
    },
  },

  ru: {
    // Navigation
    nav: {
      articles: "Изучить",
      games: "Тренировка мозга",
      exercises: "Почувствуй себя лучше",
      questionnaires: "Чек-ин",
      contact: "Связаться",
    },
    
    // Home page
    home: {
      hero: {
        title1: "Oh Mind Guide",
        title2: "\u0413\u0434\u0435 \u043d\u0435\u0439\u0440\u043e\u043d\u0430\u0443\u043a\u0430 \u0432\u0441\u0442\u0440\u0435\u0447\u0430\u0435\u0442\u0441\u044f",
        title3: "\u0441 \u043f\u043e\u0432\u0441\u0435\u0434\u043d\u0435\u0432\u043d\u043e\u0439 \u0436\u0438\u0437\u043d\u044c\u044e",
        title4: "\u0432 \u044f\u0440\u043a\u043e\u043c \u0438\u0433\u0440\u043e\u0432\u043e\u043c \u043f\u0440\u043e\u0441\u0442\u0440\u0430\u043d\u0441\u0442\u0432\u0435 \u0434\u043b\u044f \u0432\u0430\u0448\u0435\u0433\u043e \u0443\u043c\u0430",
        subtitle: "Статьи, игры, упражнения и опросники, которые помогают развивать мозг и поддерживать качество жизни — научно обоснованные и простые в использовании.",
        exploreBtn: "Исследовать Oh Mind Guide",
        contactBtn: "Связаться с нами",
      },
      heroCircle: {
        centerText: "Выбери свой путь",
        learn: "Изучить",
        learnHelper: "Короткие, понятные объяснения науки о мозге и психическом благополучии",
        trainBrain: "Тренировка мозга",
        trainBrainHelper: "Игровые задания для внимания, памяти и гибкости мышления",
        feelBetter: "Почувствуй себя лучше",
        feelBetterHelper: "Короткие практики для снижения стресса и улучшения концентрации",
        checkIn: "Чек-ин",
        checkInHelper: "Научные инструменты самооценки для настроения, сна и стресса",
      },
      about: {
        title: "Что такое Oh Mind Guide?",
        p1: "Oh Mind Guide — это увлекательное, основанное на науке пространство, где можно исследовать, как работают ваш мозг и разум, и поддерживать их активность, гибкость и устойчивость.",
        p2: "Мы превращаем открытия нейронауки и психологии в понятные объяснения и практические инструменты для повседневной жизни — в формате статей, игр, упражнений и опросников.",
        p3: "",
        brainResilience: "Устойчивость мозга",
        brainResilienceDesc: "Тренируйте внимание, память и гибкость мышления.",
        qualityOfLife: "Качество жизни",
        qualityOfLifeDesc: "Поддерживайте настроение, энергию и повседневную эффективность.",
        scienceBased: "Научная основа",
        scienceBasedDesc: "Опираемся на современные исследования в нейронауке и психологии.",
        playfulLearning: "Игровое обучение",
        playfulLearningDesc: "Лёгкий формат, который увлекает и надолго запоминается.",
      },
      founder: {
        title: "Обо мне",
        name: "Маргарита Попельнюхова",
        subtitle: "Доктор, магистр трансляционной нейронауки и основатель Oh Mind Guide.",
        description: "Я совмещаю клинический опыт с исследованиями в области науки о мозге, чтобы создавать инструменты, которые одновременно увлекательны и опираются на доказательную базу. Мне важно расширять границы нейронауки и переводить сложные идеи о мозге в простые, понятные и приятные форматы, которые легко встроить в обычный день.",
        badge1: "Доктор",
        badge2: "Магистр трансляционной нейронауки",
        badge3: "Основатель Oh Mind Guide",
      },
      who: {
        title: "Для кого Oh Mind Guide",
        parents: "Родители и педагоги",
        parentsDesc: "Помогайте детям и подросткам с помощью полезных для мозга активностей и содержательных разговоров.",
        students: "Студенты",
        studentsDesc: "Укрепляйте концентрацию, обучение и умственную выносливость с практичными, научно обоснованными инструментами.",
        adults: "Любознательные взрослые",
        adultsDesc: "Сохраняйте ум гибким, творческим и вовлечённым — легко и с удовольствием.",
        older: "Люди старшего возраста",
        olderDesc: "Поддерживайте когнитивные функции и оставайтесь ментально активными с помощью мягких, увлекательных упражнений.",
      },
      what: {
        title: "Что мы создаём",
        subtitle: "Всё, что мы делаем, опирается на доказательные методы и вдохновлено современными исследованиями в нейронауке и психологии.",
        articlesTitle: "Изучить",
        articlesDesc: "Короткие, понятные объяснения науки о мозге, связанных с ним состояний и психического благополучия — на языке повседневной жизни.",
        articlesLink: "Начать изучение →",
        gamesTitle: "Тренировка мозга",
        gamesDesc: "Игровые задания для тренировки внимания, памяти, гибкости мышления и навыков решения задач.",
        gamesLink: "Начать тренировку →",
        exercisesTitle: "Почувствуй себя лучше",
        exercisesDesc: "Короткие направленные практики для снижения стресса, улучшения концентрации и общего здоровья мозга.",
        exercisesLink: "Попробовать упражнения →",
        questionnairesTitle: "Чек-ин",
        questionnairesDesc: "Научно обоснованные инструменты самооценки, чтобы отслеживать настроение, тревожность, сон и уровень стресса.",
        questionnairesLink: "Пройти чек-ин →",
        cafeTitle: "OMG Café",
        cafeDesc: "Закажи напиток, запомни слова, пока он готовится, и проверь свою память. Уютный ритуал для ума.",
        cafeLink: "Зайти в кафе →",
        photoBoothTitle: "Фотокабина",
        photoBoothDesc: "Инструмент для запечатления моментов. Сделай 4 снимка и получи фотополоску на память.",
        photoBoothLink: "Сделать фото →",
      },
      whatsNext: {
        title: "Что дальше",
        dementiaGuideTitle: "Семейный гид по деменции",
        dementiaGuideDesc: "Скачиваемое руководство для родственников людей с деменцией — с понятными объяснениями изменений в мозге и поведении, практическими советами для повседневных ситуаций и мягкими упражнениями для поддержки и человека, и тех, кто о нём заботится.",
        dementiaGuideStatus: "В разработке",
        podcastTitle: "«От человека к человеку»",
        podcastDesc: "Видео/подкаст-серия.",
        podcastStatus: "Скоро",
        trainerTitle: "Тренажёр межполушарного взаимодействия",
        trainerDesc: "Серия игровых упражнений для укрепления связи между левым и правым полушариями мозга — поддержка координации, внимания и когнитивной гибкости.",
        trainerStatus: "В планах",
      },
      contact: {
        title: "Давайте свяжемся",
        subtitle: "Есть идея для сотрудничества, проект или вопрос? Буду рада вас услышать. Напишите, и мы посмотрим, что сможем создать вместе.",
        emailBtn: "Написать Oh Mind Guide",
      },
      faq: {
        title: "Часто задаваемые вопросы",
        q1: "Что такое Oh Mind Guide?",
        a1: "Oh Mind Guide — это научно обоснованный проект, который переводит сложные исследования из нейронауки, психологии и медицины на простой, понятный язык — чтобы ими можно было пользоваться в реальной жизни. Это мост между наукой и повседневностью: вместо расплывчатых «советов для ментального здоровья» здесь — доказательные статьи, короткие игры и упражнения, а также чек-ины (опросники), которые помогают лучше понять свой мозг, психику и привычки — без лишнего \"умного\" языка. Oh Mind Guide не про то, чтобы «чинить» себя. Он про то, чтобы разобраться, как ты устроен(а), собирать рабочие инструменты шаг за шагом и оставаться любопытным(ой). Ты всё ещё в процессе.",
        q2: "Контент основан на науке?",
        a2: "Да — контент Oh Mind Guide создаётся на основе науки. Статьи опираются на надёжные исследования в нейронауке, психологии и медицине, и по возможности сопровождаются источниками. Игры и упражнения построены на доказательных принципах того, как мозг учится, удерживает внимание, запоминает и регулирует стресс. Опросники подбираются по критериям валидности (или устойчивого использования в клинической и научной практике) и подаются как инструмент самопроверки, а не как диагноз. Важно: Oh Mind Guide — это образовательный проект для самопонимания и он не заменяет консультацию или лечение у специалиста.",
        q3: "Кто создаёт контент?",
        a3: "Весь контент на Oh Mind Guide создаёт Маргарита Попельнюхова — врач, магистр трансляционной нейронауки и основательница Oh Mind Guide.",
        q4: "Oh Mind Guide бесплатен?",
        a4: "Да, все статьи, игры для мозга, упражнения и опросники на Oh Mind Guide полностью бесплатны. Мы считаем, что каждый должен иметь доступ к научно обоснованным ресурсам для здоровья мозга.",
        q5: "Как вы выбираете источники и исследования для Oh Mind Guide?",
        a5: "Я опираюсь на надежные, доказательные источники. По возможности я отдаю приоритет систематическим обзорам и мета-анализам, крупным хорошо спланированным исследованиям, а также авторитетным клиническим рекомендациям и консенсусным документам. Я также проверяю, воспроизводятся ли результаты, насколько сильный эффект и применимы ли выводы к реальной жизни. Для опросников по ментальному здоровью я использую валидированные, широко применяемые инструменты и указываю первоисточники. Я стараюсь точно пересказывать научные данные простым языком и не делать заявлений, которые выходят за рамки доказательств.",
        q6: "Как часто обновляется контент?",
        a6: "Ключевые страницы я пересматриваю регулярно и обновляю, когда появляются новые надежные данные. В базовом режиме я стараюсь проверять и освежать основные гайды и самые посещаемые страницы минимум раз в 1–2 месяца, а также обновляю старые статьи, если важные исследования или клинические рекомендации меняют картину. При обновлении я пересматриваю источники и корректирую выводы, чтобы они оставались актуальными и точными. На страницах, которые прошли ревизию, будет указана дата \"Last updated\".",
      },
      footer: {
        copyright: "© {year} OMG GTM Solutions L.L.C-FZ. Все права защищены.",
      },
      newHere: {
        title: "Впервые здесь?",
        whatIsTitle: "Что такое Oh Mind Guide",
        whatIsDesc: "Научно обоснованная площадка для когнитивного благополучия.",
        whatIsLink: "Узнать больше",
        aboutTitle: "О создателе",
        aboutDesc: "Маргарита Попельнюхова, врач + магистр трансляционной нейронауки.",
        aboutLink: "Моя история",
        faqTitle: "FAQ и стандарты",
        faqDesc: "Как отбирается контент, как часто обновляется, дисклеймеры.",
        faqLink: "Открыть FAQ",
      },
      backToHome: "На главную",
    },
    
    // Articles page
    articles: {
      title: "Изучить",
      subtitle: "Короткие, понятные объяснения науки о мозге, связанных с ним состояний и психического благополучия — переведённые на язык повседневности.",
      // Category groups for filtering
      categories: {
        lifestyle: "Образ жизни",
        foundations: "Основы",
        brainMentalHealth: "Мозг и психическое здоровье",
      },
      backToAllArticles: "Ко всем статьям",
      readArticle: "Читать статью →",
      comingSoon: "Скоро",
      moreArticles: "Скоро появятся новые статьи",
      moreArticlesDesc: "Мы работаем над новыми научными руководствами о здоровье мозга, обучении, настроении и старении. Следите за обновлениями!",
      filterByTopic: "Фильтр по теме:",
      disclaimer: "Дисклеймер",
      disclaimerText: "Эти статьи предназначены только для общего ознакомления и не заменяют консультацию врача или обследование. Если вы беспокоитесь о себе или близком человеке, пожалуйста, обратитесь к квалифицированному специалисту.",
      clearFilters: "Сбросить фильтры",
      
      // Tags
      tags: {
        foundations: "Основы",
        sleep: "Сон",
        stress: "Стресс",
        mentalHealth: "Психическое здоровье",
        movement: "Движение",
        neurodevelopment: "Нейроразвитие",
        ageing: "Старение",
        neurodegeneration: "Нейродегенерация",
      },
      
      brainAndMind: {
        title: "Мозг и разум",
        desc: "Как физическая ткань порождает мысли, чувства и самосознание",
        badge1: "Основы",
        badge2: "Сознание",
        subtitle: "Как физическая ткань порождает мысли, чувства и самосознание",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача или обследование. Если вы беспокоитесь о себе или близком человеке, пожалуйста, обратитесь к квалифицированному специалисту.",
        backToArticles: "Ко всем статьям",
      },
      dementia: {
        title: "Деменция",
        desc: "Что это такое, почему возникает и что мы можем сделать",
        badge1: "Здоровье мозга",
        badge2: "Старение",
        subtitle: "Что это такое, почему возникает и что мы можем сделать",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача или обследование. Если вы беспокоитесь о себе или близком человеке, пожалуйста, обратитесь к квалифицированному специалисту.",
        backToArticles: "Ко всем статьям",
      },
      parkinsons: {
        title: "Болезнь Паркинсона",
        desc: "Понимание основ, науки и перспектив",
        badge1: "Здоровье мозга",
        badge2: "Движение",
      },
      adhd: {
        title: "СДВГ",
        desc: "Не только «гиперактивность»: как СДВГ влияет на мозг",
        badge1: "Здоровье мозга",
        badge2: "Нейроразвитие",
        badge3: "Образование",
        subtitle: "Нейроразвитийное расстройство с трудностями внимания и импульсивностью",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача или обследование. Если вы беспокоитесь о себе или близком человеке, пожалуйста, обратитесь к квалифицированному специалисту.",
      },
      sleep: {
        title: "Сон и мозг",
        desc: "Как сон поддерживает память, настроение и здоровье мозга на протяжении жизни",
        badge1: "Здоровье мозга",
        badge2: "Наука о сне",
        subtitle: "Стадии сна, консолидация памяти, «очистка» мозга и рекомендации для разных возрастов",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача или обследование. Если вы беспокоитесь о себе или близком человеке, пожалуйста, обратитесь к квалифицированному специалисту.",
      },
      autism: {
        title: "Расстройство аутистического спектра",
        desc: "Что это, что мы знаем о мозге и как поддержать аутичных людей",
        badge1: "Здоровье мозга",
        badge2: "Нейроразвитие",
        subtitle: "Что это, что мы знаем о мозге и как поддержать аутичных людей на протяжении жизни",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача или психолога. Если вы беспокоитесь о себе или близком человеке, пожалуйста, обратитесь к квалифицированному специалисту.",
      },
      schizophrenia: {
        title: "Шизофрения",
        desc: "Симптомы, наука о мозге, факторы риска и лечение, ориентированное на выздоровление",
        badge1: "Здоровье мозга",
        badge2: "Психическое здоровье",
        subtitle: "Симптомы, наука о мозге, факторы риска и лечение, ориентированное на выздоровление",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача. Если вы беспокоитесь о себе или близком человеке, пожалуйста, обратитесь к квалифицированному специалисту или экстренным службам в вашей стране.",
      },
      anxiety: {
        title: "Тревожность",
        desc: "Когда система тревоги мозга застревает в режиме «высокой готовности»",
        badge1: "Здоровье мозга",
        badge2: "Психическое здоровье",
        subtitle: "Когда система тревоги мозга застревает в режиме «высокой готовности»",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача или психолога. Если вы беспокоитесь о себе или близком человеке, пожалуйста, обратитесь к квалифицированному специалисту. При непосредственной угрозе жизни или здоровью обратитесь в экстренные службы или на линию психологической помощи.",
      },
      depression: {
        title: "Депрессия",
        desc: "Когда система настроения мозга застревает на «низких оборотах»",
        badge1: "Здоровье мозга",
        badge2: "Психическое здоровье",
        subtitle: "Когда система настроения мозга застревает на «низких оборотах»",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача или психолога. Если вы или кто-то из ваших близких находится в группе риска самоповреждения или суицида, пожалуйста, срочно обратитесь в экстренные службы или на линию кризисной помощи.",
      },
      ptsd: {
        title: "ПТСР",
        desc: "Понимание травмы, мозга и путей к восстановлению",
        badge1: "Здоровье мозга",
        badge2: "Психическое здоровье",
        subtitle: "Понимание травмы, мозга и путей к восстановлению",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача или психолога. Если вы переживаете симптомы травмы, пожалуйста, обратитесь к квалифицированному специалисту в области психического здоровья.",
      },
      feelingsEmotions: {
        title: "Чувства и эмоции",
        desc: "Как мозг создаёт и регулирует эмоциональный опыт",
        badge1: "Основы",
        badge2: "Психическое здоровье",
        subtitle: "Как мозг создаёт и регулирует эмоциональный опыт",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет профессиональную консультацию.",
      },
      senseOrgans: {
        title: "Органы чувств",
        desc: "Как мы воспринимаем мир через наши органы чувств",
        badge1: "Основы",
        badge2: "Наука о мозге",
        subtitle: "Как мы воспринимаем мир через наши органы чувств",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет профессиональную консультацию.",
      },
      painNociception: {
        title: "Боль и ноцицепция",
        desc: "Разница между восприятием опасности и ощущением боли",
        badge1: "Основы",
        badge2: "Наука о мозге",
        subtitle: "Разница между восприятием опасности и ощущением боли",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача.",
      },
      microbiota: {
        title: "Микробиота и нервная система",
        desc: "Как микробы кишечника общаются с мозгом и влияют на настроение, когниции и болезни",
        badge1: "Основы",
        badge2: "Наука о мозге",
        subtitle: "Как микробы кишечника общаются с мозгом и влияют на настроение, когниции и болезни",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача.",
      },
      coffeeTea: {
        title: "Кофе, чай и мозг",
        desc: "Как кофеин и полифенолы влияют на бодрость, сон и здоровье мозга в долгосрочной перспективе",
        badge1: "Основы",
        badge2: "Образ жизни",
        subtitle: "Как кофеин и полифенолы влияют на бодрость, сон и здоровье мозга в долгосрочной перспективе",
        disclaimer: "Эта статья предназначена только для общего ознакомления и не заменяет консультацию врача.",
      },
    },
    
    // Games page
    games: {
      title: "Тренировка мозга",
      subtitle: "Игровые задания для тренировки внимания, памяти, гибкости мышления и навыков решения задач.",
      // Category groups for filtering
      categories: {
        memory: "Память",
        attentionInhibition: "Внимание и торможение",
        speedFluency: "Скорость и беглость",
        visuoSpatial: "Зрительно-пространственные",
        fun: "Развлечения",
      },
      disclaimer: "Дисклеймер: Эти игры предназначены для обучения и развлечения, а не для медицинской диагностики. Если вы беспокоитесь о своём здоровье, пожалуйста, обратитесь к врачу.",
      backToHome: "На главную",
      backToGames: "Назад",
      playGame: "Играть",
      comingSoon: "Скоро",
      moreGamesDesc: "Новые игры уже в разработке! Следите за обновлениями.",
      bestScore: "Лучший результат",
      playAgain: "Играть снова",
      start: "Начать",
      
      reactionDash: {
        title: "Быстрая реакция",
        desc: "Проверьте, насколько быстро вы нажимаете на кнопку после сигнала.",
        duration: "1-3 мин",
        instructions: "Нажимайте на кнопку как можно быстрее, когда она станет зелёной. Не нажимайте раньше времени!",
        startBtn: "Начать игру",
        trial: "Попытка",
        of: "из",
        waitForGreen: "Ждите зелёный...",
        clickNow: "НАЖИМАЙ!",
        tooEarly: "Слишком рано! Эта попытка не засчитывается.",
        getReady: "Приготовьтесь к следующей попытке...",
        yourResults: "Ваши результаты",
        averageTime: "Среднее время",
        bestTime: "Лучшее время",
        falseStarts: "Фальстартов",
        feedback: {
          excellent: "Молниеносные рефлексы!",
          good: "Отличное время реакции!",
          ok: "Хорошая попытка!",
        },
        snapshot: "Это лишь мгновенный снимок вашей скорости обработки информации. Многие факторы влияют на время реакции.",
        interestingFact: "Интересный факт",
        factText: "В простых задачах на время реакции, подобных этой, многие взрослые реагируют всего за несколько сотен миллисекунд. Это меньше половины секунды от момента видения сигнала до нажатия кнопки. На время реакции влияют сон, стресс, отвлечение и даже знакомство с задачей.",
        tipTitle: "Совет для улучшения",
        tipText: "Для поддержания быстрых и надёжных реакций сосредоточьтесь на основах: хороший сон, регулярное движение и снижение отвлечений, когда нужно реагировать быстро (например, не пользуйтесь телефоном за рулём). Практика в спокойной, сосредоточенной обстановке также помогает сделать реакции более автоматическими.",
        playAgain: "Играть снова",
        backToGames: "Назад",
      },
      colourWord: {
        title: "Цветовая путаница",
        desc: "Смотрите на цвет слова, а не на его смысл.",
        duration: "1-3 мин",
        disclaimer: "Дисклеймер: Эта игра предназначена для развлечения и обучения, а не для медицинской диагностики. При проблемах со здоровьем обратитесь к врачу.",
        howToPlay: "Как играть",
        instructionsText: "Вы увидите слова КРАСНЫЙ, СИНИЙ, ЗЕЛЁНЫЙ в разных цветах.",
        taskText: "Ваша задача: нажмите кнопку, соответствующую цвету текста, а не слову.",
        exampleText: "Например, если вы видите",
        clickText: ", нажмите СИНИЙ.",
        startBtn: "Начать",
        trialOf: "Попытка {current} из {total}",
        clickInkColor: "Нажмите на цвет чернил:",
        yourResults: "Ваши результаты",
        correct: "Правильно",
        mistakes: "Ошибок",
        averageReactionTime: "Среднее время реакции (правильные ответы)",
        matchingTrials: "Совпадающие попытки",
        conflictingTrials: "Конфликтные попытки",
        excellentControl: "Отличный когнитивный контроль!",
        goodPerformance: "Хороший результат!",
        challenging: "Непросто, правда?",
        suppressUrgeText: "Вашему мозгу пришлось подавить желание прочитать слово и вместо этого обращать внимание на цвет текста.",
        interestingFact: "Интересный факт",
        factText: "«Эффект Струпа» используется в психологии уже много десятилетий, чтобы показать, как наш мозг автоматически читает слова, даже когда мы пытаемся сосредоточиться на чём-то другом, например, на цвете текста. Это простой пример того, как автоматические привычки могут мешать тому, на что мы хотим обратить внимание.",
        tipTitle: "Совет для улучшения",
        tipText: "Вы можете поддержать этот вид концентрации, практикуя одну задачу за раз (вместо интенсивной многозадачности), давая мозгу короткие перерывы и используя небольшие подсказки, например отключение уведомлений, когда нужно сосредоточиться на одной вещи.",
        playAgain: "Играть снова",
        backToGames: "Назад",
      },
      numberEcho: {
        title: "Эхо чисел",
        desc: "Запоминайте и воспроизводите всё более длинные последовательности чисел.",
        duration: "2-5 мин",
        disclaimer: "Это упражнение для обучения и саморефлексии, а не медицинская диагностика.",
        howToPlay: "Как играть",
        instructionsText: "Вы увидите последовательность цифр, появляющихся по одной.",
        taskText: "Внимательно смотрите и введите их в том же порядке!",
        startsWithText: "Начните с 3 цифр. Каждый раз, когда вы отвечаете правильно, последовательность удлиняется на одну цифру.",
        endsText: "Игра заканчивается, когда вы делаете ошибку или достигаете максимальной длины.",
        startGame: "Начать игру",
        sequenceLength: "Длина последовательности:",
        watchCarefully: "Внимательно смотрите...",
        yourTurn: "Ваша очередь",
        typeDigits: "Введите цифры, которые вы видели (без пробелов)",
        enterDigits: "Введите цифры...",
        submit: "Отправить",
        yourResults: "Ваши результаты",
        maxDigitSpan: "Максимальный размах цифр",
        roundsCompleted: "Пройдено раундов",
        goodStart: "Хорошее начало!",
        strongMemory: "Сильная рабочая память!",
        impressiveMemory: "Впечатляющий объём памяти!",
        averageSpanText: "Средний размах цифр у взрослых составляет 5-7 цифр. Небольшие вариации совершенно нормальны и не отражают общий интеллект или способности.",
        interestingFact: "Интересный факт",
        factText: "Рабочая память — это система мозга, которая позволяет удерживать и манипулировать несколькими элементами информации одновременно, как ментальный блокнот. Исследования показывают, что обычно мы можем держать активными лишь небольшое количество элементов, поэтому даже простые отвлечения могут заставить нас забыть только что услышанный номер телефона.",
        tipTitle: "Совет для улучшения",
        tipText: "Для поддержки рабочей памяти старайтесь уменьшать фоновые отвлечения, разбивать информацию на более мелкие «куски» (например, 1-9-8-4 как две пары вместо четырёх отдельных чисел) и давать мозгу достаточно сна и коротких перерывов при выполнении сложных задач.",
        playAgain: "Играть снова",
        backToGames: "Назад",
      },
      wordSprint: {
        title: "Словесный спринт",
        desc: "Напишите как можно больше разных слов за 60 секунд.",
        duration: "1-2 мин",
        disclaimer: "Дисклеймер: Эта игра предназначена для развлечения и обучения, а не для медицинской диагностики. При проблемах со здоровьем обратитесь к врачу.",
        howToPlay: "Как играть",
        instructionsText: "Выберите категорию и введите как можно больше разных слов за 60 секунд.",
        typeOneWord: "Вводите по одному слову и нажимайте Enter. Каждое уникальное слово засчитывается!",
        chooseCategory: "Выберите категорию:",
        category: "Категория",
        wordsEntered: "Слов введено",
        seconds: "секунд",
        typeWord: "Введите слово...",
        add: "Добавить",
        alreadyUsed: "Вы уже использовали это слово!",
        yourWords: "Ваши слова:",
        totalWords: "Всего слов",
        yourResults: "Ваши результаты",
        excellentFluency: "Ваш словарный двигатель разогрелся!",
        goodFluency: "Отличная беглость!",
        okFluency: "Хорошая попытка!",
        feedbackText: "Быстрые словесные задачи используют языковые сети мозга и «исполнительные» системы, которые помогают искать, переключаться и избегать повторов.",
        interestingFact: "Интересный факт",
        factText: "Тесты на вербальную беглость широко используются в психологии и нейронауке для оценки извлечения слов из памяти, умственной гибкости и исполнительного контроля. Ваша способность быстро генерировать слова зависит от того, насколько эффективно ваш мозг может искать в семантической памяти и фильтровать повторяющиеся ответы.",
        tipTitle: "Совет для улучшения",
        tipText: "Для поддержки этого типа мышления помогает широкое чтение, изучение новых слов на любом языке и творческие занятия (например, рассказывание историй или игры), которые поддерживают языковые сети активными и гибкими.",
        playAgain: "Играть снова",
        backToGames: "Назад",
        categories: {
          animals: "Животные",
          foods: "Еда",
          roundThings: "Круглые предметы",
          kitchenThings: "Вещи на кухне",
          wearableThings: "Что можно носить",
          vehicles: "Транспорт",
          sportsGames: "Спорт или игры",
          emotions: "Эмоции/чувства",
          jobs: "Профессии",
          cityPlaces: "Места в городе",
          countries: "Страны",
          weather: "Погода",
        },
      },
      memoryGrid: {
        title: "Сетка памяти",
        desc: "Запоминайте расположение подсвеченных клеток и повторяйте узор.",
        duration: "1-3 мин",
        disclaimer: "Дисклеймер: Эта игра предназначена для развлечения и обучения, а не для медицинской диагностики. При проблемах со здоровьем обратитесь к врачу.",
        howToPlay: "Как играть",
        instructionsText: "Смотрите на паттерн подсвеченных квадратов, затем нажмите на те же квадраты по памяти.",
        eachLevel: "На каждом уровне добавляется ещё один квадрат. Сетка может увеличиваться по мере прогресса!",
        level: "Уровень",
        watchCarefully: "Внимательно смотрите...",
        selectSquares: "Выберите {count} квадратов",
        selected: "Выбрано",
        correct: "Правильно!",
        yourResults: "Ваши результаты",
        highestLevel: "Высший уровень",
        largestPattern: "Наибольший паттерн",
        excellentMemory: "Выдающаяся пространственная память!",
        goodMemory: "Отличный результат!",
        okMemory: "Хорошее начало!",
        reachedLevel: "Вы достигли уровня {level} – это {pattern} квадратов для запоминания!",
        interestingFact: "Интересный факт",
        factText: "Эта игра задействует зрительно-пространственную рабочую память — вашу способность удерживать в уме местоположения и паттерны в течение нескольких секунд. Это похоже на запоминание того, где вы оставили вещи в комнате, или на ориентирование в новом месте. Исследования показывают, что зрительно-пространственная память задействует сети в теменной и лобной долях мозга.",
        tipTitle: "Совет для улучшения",
        tipText: "Для поддержки зрительно-пространственной памяти в повседневной жизни используйте как визуальные, так и вербальные подсказки (например, описывайте маршруты вслух), уменьшайте многозадачность, когда нужно запомнить расположение вещей, и поддерживайте порядок в важных местах. Регулярная практика пространственных задач и качественный сон тоже помогают.",
        playAgain: "Играть снова",
        backToGames: "Назад",
      },
      quickReflex: {
        title: "Быстрый рефлекс",
        desc: "Проверьте скорость зрительной реакции",
        duration: "Скоро",
      },
      focusTap: {
        title: "Цели и ловушки",
        desc: "Касайтесь целей, игнорируйте ловушки.",
        duration: "1 мин",
        disclaimer: "Дисклеймер: Эта игра предназначена для развлечения и обучения, а не для медицинской диагностики. При проблемах со здоровьем обратитесь к врачу.",
        tapForTarget: "Касайтесь целевой фигуры и игнорируйте всё остальное.",
        roundLength: "Длительность раунда: 60 секунд",
        target: "ЦЕЛЬ",
        traps: "ЛОВУШКИ (игнорируйте)",
        greenCircle: "Зелёный круг",
        otherShapes: "Другие фигуры/цвета",
        howToPlay: "Как играть",
        shapesAppear: "Фигуры будут появляться по одной в центре",
        tapOrClick: "Касайтесь/нажимайте или используйте пробел ТОЛЬКО для зелёного круга",
        doNothing: "Ничего не делайте для других фигур",
        paceSpeedsUp: "Темп ускоряется по ходу игры!",
        startGame: "Начать игру",
        getReady: "Приготовьтесь...",
        hits: "Попадания",
        falseAlarms: "Ложные тревоги",
        tapHint: "Касайтесь области фигуры или нажимайте пробел только для зелёных кругов",
        yourResults: "Ваши результаты",
        roundComplete: "Раунд завершён!",
        accuracy: "Точность",
        focusTapScore: "Очки Focus Tap",
        avgRT: "Среднее ВР",
        excellentFocus: "Отличный фокус и контроль импульсов! 🎯",
        goodFocus: "Хорошая концентрация! 👍",
        challengingFocus: "Непросто, правда? Ловушки бывает сложно игнорировать!",
        whatThisTrains: "Что тренирует эта игра",
        trainsText: "Это задача «Go/No-Go». Она тренирует торможение реакции — вашу способность останавливать автоматический ответ, когда это необходимо. Этот навык важен для контроля импульсов, принятия решений и саморегуляции в повседневной жизни.",
        interestingFact: "Интересный факт",
        factText: "Торможение реакции связано с правой префронтальной корой. Исследования показывают, что даже небольшие улучшения тормозного контроля могут помочь с концентрацией, эмоциональной регуляцией и сопротивлением отвлечениям.",
        tipTitle: "Советы для улучшения",
        tipText: "Практикуйте замедление перед действием в повседневной жизни. Пауза даже на секунду перед реакцией может укрепить «стоп»-схему вашего мозга. Упражнения на осознанность, достаточный сон и избегание усталости от принятия решений также поддерживают лучший контроль импульсов.",
        playAgain: "Играть снова",
        backToGames: "Назад",
      },
      mathMinute: {
        title: "Математическая минута",
        desc: "Минута быстрой арифметики: сложение, вычитание, умножение, деление.",
        duration: "1 мин",
        correctLabel: "верно",
      },
      memoryCard: {
        title: "Найди пару",
        desc: "Классическая игра на память — найди все пары карточек.",
        duration: "2-5 мин",
        attemptsLabel: "попыток",
      },
      numberSequence: {
        title: "Числовой путь",
        desc: "Нажимайте числа от 1 до 25 по порядку как можно быстрее.",
        duration: "1-2 мин",
      },
      targetShift: {
        title: "Сдвиг прицела",
        desc: "Мозг перенастраивает движения, когда обратная связь меняется.",
        duration: "2-4 мин",
      },
      changeBlindness: {
        title: "Слепота к изменениям",
        desc: "Найдите единственное изменение после вспышки.",
        duration: "1-2 мин",
      },
      
      // Extras
      extrasTitle: "Бонусы",
      visitCafe: "Зайти в кафе",
      takePhotos: "Сделать фото",
      cafe: {
        title: "OMG Café",
        desc: "Закажи напиток, запомни слова, пока он готовится, и проверь свою память.",
        duration: "5-10 мин",
      },
      photoBooth: {
        title: "Фотокабина",
        desc: "Инструмент для запечатления моментов. Сделай 4 снимка и получи фотополоску на память.",
        duration: "Просто для веселья",
      },
    },
    
    // Exercises page
    exercises: {
      title: "Почувствуй себя лучше",
      subtitle: "Короткие направленные практики для снижения стресса, улучшения концентрации и общего здоровья мозга.",
      disclaimer: "Дисклеймер: Эти упражнения предназначены для обучения и самопомощи, а не для медицинской диагностики или лечения. Если у вас есть опасения по поводу здоровья, пожалуйста, обратитесь к специалисту.",
      backToExercises: "Назад",
      backToHome: "На главную",
      startExercise: "Начать упражнение",
      comingSoon: "Скоро",
      moreExercisesDesc: "Новые упражнения уже в разработке! Следите за обновлениями.",
      practiceAgain: "Повторить",
      completeExercise: "Завершить упражнение",
      youCanComplete: "Вы можете завершить в любой момент",
      
      breathReset: {
        title: "Перезагрузка дыхания",
        tagline: "Дышите в такт подсказкам на экране, чтобы замедлиться и немного успокоиться.",
        duration: "3 минуты",
        description: "3-минутная практика дыхания, где вы следуете за движущимся квадратом: вдох, задержка, выдох, задержка.",
        explanation: "Эта простая техника «квадратного дыхания» используется во многих клинических и спортивных условиях для успокоения нервной системы. Вы будете следить за движущимся квадратом на экране. Каждая сторона квадрата направляет одну часть дыхания: вдох, задержка, выдох, задержка.",
        safetyNote: "Если в какой-то момент вы почувствуете головокружение или дискомфорт, остановите упражнение и вернитесь к обычному дыханию.",
        whatTrainsTitle: "Что тренирует:",
        whatTrains1: "Плавно замедляет дыхание и сердцебиение",
        whatTrains2: "Помогает перейти в состояние «отдыха и восстановления»",
        whatTrains3: "Развивает осознанность телесных ощущений и дыхания",
        inhale: "Вдох",
        hold: "Держите",
        exhale: "Выдох",
        paused: "Пауза",
        pause: "Пауза",
        resume: "Продолжить",
        reset: "Сброс",
        instructions: "Следите глазами за движущейся линией. Вдыхайте, когда квадрат показывает «Вдох», задерживайте дыхание на «Держите», выдыхайте на «Выдох» и снова задерживайте. Старайтесь дышать мягко и плавно, без напряжения.",
        nicelyDone: "Отлично",
        completedMsg: "Вы только что дали своей нервной системе короткую перезагрузку.",
        noticeMsg: "Заметьте, как сейчас чувствует себя ваше тело – есть ли изменения в напряжении, тепле или спокойствии?",
        cyclesCompleted: "Вы завершили {count} полных циклов дыхания",
      },
      awarenessCheckIn: {
        title: "Проверка осознанности",
        subtitle: "Ум, Тело, Настроение",
        tagline: "На пару минут остановитесь и заметьте, что происходит с умом, телом и настроением, не пытаясь ничего менять.",
        duration: "2 минуты",
        description: "2-минутная проверка, чтобы заметить, что происходит в вашем теле, уме и настроении – без попыток что-то исправить.",
        explanation: "Осознанность – это первый шаг к мягким изменениям. Это упражнение поможет сделать небольшой «снимок» вашего внутреннего мира – как ощущается тело, чем занят ум и каково ваше общее настроение прямо сейчас.",
        whatTrainsTitle: "Что тренирует:",
        whatTrains1: "Эмоциональную осознанность",
        whatTrains2: "Интероцепцию (ощущение тела изнутри)",
        whatTrains3: "Саморегуляцию",
        startCheckIn: "Начать проверку",
        body: "Тело",
        bodyScan: "Просканируйте тело от головы до пальцев ног. Заметьте любое напряжение, беспокойство, тепло или расслабление – не пытаясь ничего менять.",
        overallTension: "Общее напряжение тела",
        tensionHint: "1 = очень напряжено, 10 = очень расслаблено",
        veryTense: "Очень напряжено",
        inBetween: "Средне",
        veryRelaxed: "Очень расслаблено",
        whereTension: "Где напряжение? (выберите все подходящие)",
        tensionAreas: {
          headJaw: "Голова / челюсть",
          neck: "Шея",
          shoulders: "Плечи",
          chest: "Грудь",
          stomach: "Живот",
          upperBack: "Верхняя часть спины",
          lowerBack: "Нижняя часть спины",
          armsHands: "Руки / кисти",
          legsFeet: "Ноги / ступни",
          wholeBody: "Всё тело",
          notSure: "Не уверен(а)",
        },
        mind: "Ум",
        mindNotice: "Заметьте, где ваш ум проводит большую часть времени прямо сейчас. В основном в прошлом, настоящем или будущем?",
        past: "Прошлое",
        present: "Настоящее",
        future: "Будущее",
        noRightAnswer: "Здесь нет правильного ответа – просто заметьте, где обитает ваш ум.",
        thoughtsLabel: "Хотите записать мысли? (необязательно)",
        thoughtsPlaceholder: "Что сейчас в голове...",
        mood: "Настроение",
        moodQuestion: "Если бы ваше настроение сейчас было цветом и числом, какими бы они были?",
        chooseColor: "Выберите цвет, который соответствует вашему настроению",
        selected: "Выбрано",
        overallMood: "Общее настроение",
        veryLow: "Очень низкое",
        veryHigh: "Очень высокое",
        moodDescLabel: "Опишите настроение словами (необязательно)",
        moodDescPlaceholder: "Как бы вы описали своё состояние...",
        completeCheckIn: "Завершить проверку",
        addPhoto: "Добавить фото (необязательно)",
        addPhotoDesc: "Запечатлейте момент – себя или окружение",
        takePhoto: "Сделать фото",
        removePhoto: "Удалить",
        snapshotTitle: "Ваш снимок прямо сейчас",
        notSpecified: "Не указано",
        notSelected: "Не выбрано",
        mostlyIn: "В основном в",
        notYetSelected: "Ещё не выбрано.",
        snapshotNote1: "Это просто снимок – ваше внутреннее состояние меняется всё время, даже в течение одного дня.",
        snapshotNote2: "Такие проверки помогают мозгу замечать закономерности со временем, без осуждения.",
        completeTitle: "Ваша проверка осознанности",
        completeMsg: "Ничего не нужно менять прямо сейчас – просто заметить – это уже мощный шаг для вашего мозга и психического здоровья.",
        checkInAgain: "Проверить снова",
        thoughts: "Мысли",
        moodDescription: "Настроение словами",
        moodColors: {
          grey: "Мягкий серый",
          blue: "Спокойный синий",
          green: "Свежий зелёный",
          yellow: "Тёплый жёлтый",
          orange: "Яркий оранжевый",
          red: "Глубокий красный",
          purple: "Нежный фиолетовый",
        },
      },
      fiveSenseGrounding: {
        title: "Заземление через пять чувств",
        tagline: "Вернитесь в настоящий момент, по очереди замечая, что вы видите, слышите, ощущаете кожей, какие чувствуете запахи и вкусы.",
        duration: "3 минуты",
        description: "Короткое упражнение заземления, которое использует ваши пять чувств, чтобы вернуть вас в настоящий момент.",
        explanation: "Когда мы в стрессе или перегружены, ум может закручиваться в беспокойства о прошлом или будущем. Это упражнение мягко возвращает внимание к «здесь и сейчас», направляя вас через то, что вы видите, чувствуете, слышите, обоняете и ощущаете на вкус вокруг себя.",
        whatTrainsTitle: "Что тренирует:",
        whatTrains1: "Заземление в настоящем моменте",
        whatTrains2: "Снижение стресса",
        whatTrains3: "Сенсорную осознанность",
        pattern: "5 – 4 – 3 – 2 – 1",
        see5: "5 вещей, которые вы видите",
        see5Desc: "Осмотритесь и мысленно назовите пять вещей, которые видите. Они могут быть простыми: кружка, тень, книга, цвет, ваши руки.",
        feel4: "4 вещи, которые вы чувствуете",
        feel4Desc: "Заметьте четыре вещи, которые вы чувствуете через прикосновение – например, ноги на полу, одежду на коже, воздух на лице или текстуру предмета рядом.",
        hear3: "3 вещи, которые вы слышите",
        hear3Desc: "Внимательно прислушайтесь к трём звукам – близким или далёким. Это может быть транспорт, гул холодильника, птицы, вентилятор компьютера, ваше дыхание.",
        smell2: "2 вещи, которые вы обоняете",
        smell2Desc: "Заметьте два запаха. Если сейчас ничего не чувствуете – это нормально. Можно приблизиться к чему-то с запахом (мыло, кофе, крем для рук) или просто заметить «нейтральный» запах комнаты.",
        taste1: "1 вещь, которую вы ощущаете на вкус",
        taste1Desc: "Заметьте одну вещь, которую вы ощущаете на вкус. Это может быть послевкусие напитка, зубная паста или просто естественный вкус во рту. Если сложно, можно представить вкус, который вам нравится.",
        grounded: "Вы заземлены",
        groundedMsg: "Вы только что закрепили своё внимание в настоящем моменте с помощью чувств.",
        groundedNotice: "Заметьте, чувствует ли ваше тело или ум себя хотя бы немного иначе по сравнению с началом.",
      },
      panicSOS: {
        title: "Паника SOS – Оседлай волну",
        tagline: "Следуйте пошаговому плану, чтобы пережить волну сильной тревоги чуть более безопасно и устойчиво.",
        duration: "3–5 минут",
        description: "Краткое пошаговое руководство, которое поможет пережить волну сильной тревоги или лёгкой паники, пока она не пройдёт.",
        explanation: "Паника и сильная тревога могут ощущаться очень интенсивно и пугающе, но сами по себе эти ощущения обычно не опасны. Это упражнение помогает остаться с волной и позволить ей подняться, достичь пика и спасть.",
        safetyTitle: "Важное замечание о безопасности",
        safetyText: "Это упражнение предназначено для лёгкой или умеренной паники или высокой тревоги. Если у вас боль в груди, затруднённое дыхание, вы чувствуете, что можете потерять сознание, думаете, что у вас сердечный приступ, или у вас есть мысли о причинении себе вреда – немедленно обратитесь за срочной медицинской помощью.",
        step1: "Назовите волну",
        step1Desc: "Кратко назовите, что происходит. Не нужно деталей – просто короткая фраза.",
        step1Placeholder: "Паника в метро, Тревога перед встречей...",
        intensityNow: "Насколько интенсивно это ощущается прямо сейчас?",
        intensityScale: "0 = очень слабо, 10 = крайне сильно",
        step2: "Укрепите тело",
        step2Desc: "Помогите нервной системе почувствовать, где ваше тело поддерживается прямо сейчас.",
        groundingOptions: {
          feet: "Обе ступни плоско на полу",
          back: "Спина опирается на стул или стену",
          hands: "Руки мягко сжаты вместе",
          ground: "Замечаю контакт с полом или сиденьем",
        },
        chooseWhatPossible: "Не нужно делать всё – просто выберите то, что возможно для вас сейчас.",
        step3: "Смягчите дыхание",
        step3Desc: "Позвольте дыханию замедлиться мягко, без усилий.",
        inhaleFor: "Вдохните через нос примерно на 4 секунды.",
        exhaleFor: "Выдохните через рот примерно на 6 секунд, как будто медленно задуваете свечу.",
        breathsCompleted: "Вдохов сделано",
        of: "из",
        start: "Начать",
        breath: "+1 Вдох",
        step4: "Осмотритесь",
        step4Desc: "Позвольте глазам осмотреть пространство вокруг. Мягко назовите, что действительно здесь, прямо сейчас.",
        thingsISee: "3 вещи, которые я вижу",
        thingsIFeel: "2 вещи, которые я чувствую (прикосновение)",
        thingsIHear: "1 звук, который я слышу",
        step5: "Перепроверьте волну",
        step5Desc: "Волны паники поднимаются и опускаются. Давайте посмотрим, как эта сейчас.",
        intensityBefore: "До",
        intensityAfter: "Сейчас",
        waveDecreased: "Даже небольшое изменение – это сдвиг. Молодец, что остались с волной.",
        waveSame: "Если ничего не изменилось – это тоже нормально. Волна всё равно пройдёт.",
        closingMsg: "Вы только что остались с трудной волной вместо того, чтобы бороться с ней.",
        closingNote: "Если сильная тревога или паника возвращаются часто, подумайте о консультации с психологом или психотерапевтом.",
      },
      nightTimeWorry: {
        title: "Ночная парковка тревог",
        tagline: "Запишите тревожные мысли, «припаркуйте» их до завтра и немного разгрузите голову перед сном.",
        duration: "5 минут",
        description: "Короткое упражнение, чтобы выгрузить тревоги из головы и «припарковать» их до завтра.",
        explanation: "Ночью мозг часто начинает прокручивать проблемы по кругу. Записывание тревог и их сортировка помогут уму отпустить их для сна – зная, что завтрашний вы сможете вернуться к ним.",
        step1: "Запишите свои тревоги",
        step1Desc: "В нескольких коротких строках напишите, к чему ваш ум постоянно возвращается. По одной тревоге на строку.",
        step1Placeholder: "Дедлайн на работе\nСсора с другом\nДеньги\nМедосмотр на следующей неделе...",
        next: "Далее",
        step2: "Рассортируйте тревоги",
        step2Desc: "Для каждой тревоги спросите: «Есть ли конкретное действие, которое я могу предпринять завтра?» Если да, мы отметим маленький следующий шаг. Если нет, мы пометим это как тревогу, с которой вы не можете действовать прямо сейчас.",
        worryOf: "Тревога {current} из {total}",
        canActTomorrow: "Могу действовать завтра",
        cantActNow: "Не могу действовать сейчас",
        smallestNextStep: "Самый маленький следующий шаг на завтра:",
        nextStepPlaceholder: "Отправить письмо..., Проверить счёт, Составить список...",
        previous: "Назад",
        finish: "Готово",
        step3: "Ваши тревоги припаркованы",
        step3Desc: "Вот снимок того, что вы припарковали на завтра. Мы не храним эти данные на сервере, поэтому если хотите сохранить, сделайте скриншот или скопируйте текст перед закрытием страницы.",
        actionsForTomorrow: "Действия на завтра",
        letGoTonight: "То, что можно мягко отпустить на сегодня:",
        nextStepLabel: "Следующий шаг",
        copyAll: "Скопировать всё в буфер",
        copied: "Скопировано в буфер обмена!",
        copyFailed: "Не удалось скопировать",
        closingMsg: "Вы дали своему мозгу сигнал:",
        closingMsg2: "«Эти тревоги записаны. Я могу вернуться к ним завтра.»",
        closingNote: "Ваша единственная задача сейчас – отдыхать. Если проблемы со сном или тревога очень частые или сильные, подумайте о консультации со специалистом.",
      },
      cognitiveShuffle: {
        title: "Когнитивная перетасовка",
        tagline: "Мягкая перезагрузка, когда мысли зацикливаются.",
        duration: "3–5 минут",
      },
      eisenhowerMatrix: {
        title: "\u041c\u0430\u0442\u0440\u0438\u0446\u0430 \u042d\u0439\u0437\u0435\u043d\u0445\u0430\u0443\u044d\u0440\u0430",
        tagline: "\u041f\u0440\u0438\u043e\u0440\u0438\u0442\u0438\u0437\u0438\u0440\u0443\u0439\u0442\u0435 \u0437\u0430\u0434\u0430\u0447\u0438 \u043f\u043e \u0441\u0440\u043e\u0447\u043d\u043e\u0441\u0442\u0438 \u0438 \u0432\u0430\u0436\u043d\u043e\u0441\u0442\u0438.",
        duration: "5\u201315 \u043c\u0438\u043d",
      },
      photoBooth: {
        title: "Фотокабина",
        tagline: "Инструмент для запечатления моментов.",
        duration: "Просто для удовольствия",
      },
    },
    
    // Questionnaires page
    questionnaires: {
      title: "Чек-ины",
      subtitle: "Научно обоснованные инструменты самооценки для проверки настроения, тревожности, сна и уровня стресса.",
      disclaimer: "Дисклеймер",
      disclaimerText: "Эти опросники не являются диагнозом и не заменяют консультацию специалиста. Если симптомы беспокоят вас или мешают повседневной жизни, пожалуйста, обратитесь к врачу, психологу или другому квалифицированному специалисту.",
      backToHome: "На главную",
      backToQuestionnaires: "Назад",
      returnToQuestionnaires: "Назад",
      startQuestionnaire: "Начать тест",
      estimatedTime: "Примерное время",
      comingSoon: "Скоро",
      moreQuestionnairesDesc: "Новые опросники уже в разработке! Следите за обновлениями.",
      scienceBased: "Научно обосновано",
      ageRestriction: "Возраст",
      years: "лет",
      plusYears: "+ лет",
      
      phq9: {
        title: "PHQ-9 – Проверка настроения",
        desc: "Краткий опросник, который помогает оценить симптомы сниженного настроения за последние 2 недели.",
        time: "2–5 минут",
      },
      gad7: {
        title: "GAD-7 – Проверка тревожности",
        desc: "Короткий опросник о том, как часто вы испытывали тревогу и беспокойство за последние 2 недели.",
        time: "2–4 минуты",
      },
      pss10: {
        title: "PSS-10 – Проверка стресса",
        desc: "Широко используемый опросник из 10 вопросов о том, насколько напряжённой и перегруженной казалась жизнь за последний месяц.",
        time: "3–5 минут",
      },
      maas: {
        title: "MAAS – Проверка осознанности",
        desc: "Краткий опросник о том, насколько вы замечаете происходящее в повседневной жизни, а не живёте «на автопилоте».",
        time: "3–5 минут",
      },
      psqi: {
        title: "PSQI – Проверка качества сна",
        desc: "Научно обоснованный опросник о том, как вы спали в течение последнего месяца.",
        time: "5–7 минут",
      },
      ucla: {
        title: "Шкала одиночества UCLA – Проверка связей",
        desc: "Широко используемый опросник о чувствах близости с другими людьми и одиночества.",
        time: "5–7 минут",
      },
      cbi: {
        title: "CBI – Проверка на выгорание",
        desc: "19 вопросов о том, насколько вы эмоционально и физически истощены: в личной жизни, на работе и в работе с людьми.",
        time: "5–7 минут",
      },
      criq: {
        title: "CRIq – Проверка «запаса мозга»",
        desc: "Опросник о том, как обучение, работа и хобби в течение жизни помогают накапливать «запас прочности» мозга.",
        time: "5–7 минут",
      },
      cllp: {
        title: "CLLP – Когнитивная нагрузка и профиль долголетия",
        desc: "Комплексный, научно обоснованный профиль вашего когнитивного здоровья — сон, стресс, внимание, память и многое другое. Персонализированный под ваши ответы. Применимый с первого дня.",
        time: "25 минут",
      },
    },
    
    // Common questionnaire text
    questionnaire: {
      questionOf: "Вопрос {current} из {total}",
      back: "Назад",
      next: "Далее",
      seeResults: "Посмотреть результаты",
      canStop: "Вы можете остановиться в любой момент. Ваши ответы не сохраняются постоянно.",
      outOf: "из {max} баллов",
      outOfAvg: "из {max} (средний балл)",
      exploreNext: "Следующие шаги с Oh Mind Guide",
      
      // PHQ-9
      phq9: {
        title: "PHQ-9 – Проверка настроения",
        intro: "За последние 2 недели как часто вас беспокоили следующие проблемы?",
        resultTitle: "Результат проверки настроения (PHQ-9)",
        items: [
          "Слабый интерес или отсутствие удовольствия от занятий",
          "Подавленность, депрессия или чувство безнадёжности",
          "Проблемы с засыпанием или сном, или слишком долгий сон",
          "Чувство усталости или недостатка энергии",
          "Плохой аппетит или переедание",
          "Плохое отношение к себе – или ощущение, что вы неудачник, или что подвели себя или свою семью",
          "Трудности с концентрацией, например, при чтении газеты или просмотре телевизора",
          "Движения или речь настолько замедлены, что окружающие могли это заметить. Или наоборот – такая беспокойность или суетливость, что вы двигались намного больше обычного",
          "Мысли о том, что лучше бы вас не было, или о причинении себе вреда",
        ],
        categories: {
          low: "Низкий диапазон",
          mild: "Лёгкий диапазон",
          moderate: "Умеренный диапазон",
          high: "Высокий диапазон",
          veryHigh: "Очень высокий диапазон",
        },
        descriptions: {
          low: "Ваши ответы находятся в нижнем диапазоне симптомов настроения по этому опроснику.",
          mild: "Вы отметили некоторые симптомы, связанные с настроением.",
          moderate: "Вы отметили ряд симптомов настроения, которые попадают в умеренный диапазон по этому опроснику.",
          high: "Ваш балл находится в высоком диапазоне симптомов настроения по этому опроснику.",
          veryHigh: "Ваш балл находится в очень высоком диапазоне симптомов настроения по этому опроснику.",
        },
        recommendations: {
          low: [
            "У всех бывают взлёты и падения, но сейчас вы не отмечаете многих типичных симптомов, о которых спрашивает эта шкала.",
            "Продолжайте поддерживать здоровые привычки: регулярный сон, физическую активность и социальные связи.",
            "Изучите наши статьи о здоровье мозга и мягкие игры для поддержки вашего благополучия.",
          ],
          mild: [
            "Это не означает автоматически, что у вас депрессия, но это сигнал обратить внимание на своё самочувствие.",
            "Рассмотрите мягкий уход за собой: поддерживайте регулярный режим сна, занимайтесь физической активностью и планируйте небольшие приятные занятия.",
            "Разговор с близким человеком может помочь.",
            "Если эти чувства длятся более 2 недель или усиливаются, рассмотрите возможность обратиться к специалисту.",
          ],
          moderate: [
            "Это может быть признаком того, что ваша эмоциональная нагрузка сейчас довольно велика.",
            "Мы настоятельно рекомендуем вам поговорить с врачом или специалистом по психическому здоровью в ближайшее время, особенно если симптомы длятся несколько недель или влияют на работу, учёбу или отношения.",
            "Обращение за помощью – признак серьёзного отношения к здоровью мозга, а не слабости.",
            "Продолжайте базовый уход за собой: сон, питание, мягкое движение и общение с другими.",
          ],
          high: [
            "Это говорит о том, что вы испытываете значительные эмоциональные трудности.",
            "Пожалуйста, поговорите с врачом или специалистом по психическому здоровью как можно скорее.",
            "Если симптомы включают безнадёжность, очень низкую мотивацию или мысли о самоповреждении, обращение за помощью срочно необходимо.",
            "Вы заслуживаете поддержки, и эффективное лечение доступно.",
          ],
          veryHigh: [
            "Это указывает на тяжёлые симптомы, требующие профессионального внимания.",
            "Пожалуйста, немедленно свяжитесь с врачом, психотерапевтом или службой кризисной помощи.",
            "Если у вас есть мысли о самоповреждении, пожалуйста, немедленно обратитесь в экстренные службы или на линию кризисной помощи.",
            "Восстановление возможно при правильной поддержке и лечении.",
          ],
        },
        suicideRisk: {
          title: "Срочно: пожалуйста, обратитесь за помощью",
          text: "Поскольку вы указали некоторые мысли о самоповреждении или о том, что не хотите жить, очень важно как можно скорее поговорить с кем-то.",
          ifAtRisk: "Если вы чувствуете, что можете причинить себе вред, пожалуйста, свяжитесь с:",
          contacts: [
            "Экстренные службы (112 или 911)",
            "Местная линия кризисной или суицидальной помощи",
            "Ваш врач или специалист по психическому здоровью",
            "Близкий друг или член семьи",
          ],
        },
        nextSteps: {
          articlesTitle: "Читать статьи",
          articlesDesc: "Изучите научно обоснованные руководства о настроении, сне и устойчивости мозга",
          gamesTitle: "Попробовать игры",
          gamesDesc: "Мягко тренируйте мозг с помощью увлекательных научно обоснованных упражнений",
        },
      },
      
      // GAD-7
      gad7: {
        title: "GAD-7 – Проверка тревожности",
        intro: "За последние 2 недели как часто вас беспокоили следующие проблемы?",
        resultTitle: "Результат проверки тревожности (GAD-7)",
        items: [
          "Нервозность, тревога или ощущение «на взводе»",
          "Невозможность остановить или контролировать беспокойство",
          "Чрезмерное беспокойство о разных вещах",
          "Трудности с расслаблением",
          "Такая беспокойность, что трудно усидеть на месте",
          "Лёгкая раздражительность или раздражённость",
          "Чувство страха, как будто может произойти что-то ужасное",
        ],
        categories: {
          low: "Низкий диапазон",
          mild: "Лёгкий диапазон",
          moderate: "Умеренный диапазон",
          high: "Высокий диапазон",
        },
        descriptions: {
          low: "Ваши ответы указывают на низкий уровень симптомов тревоги по этому опроснику.",
          mild: "Вы отметили некоторые признаки тревоги или беспокойства.",
          moderate: "Ваши ответы находятся в умеренном диапазоне симптомов тревоги по этому опроснику.",
          high: "Ваш балл находится в высоком диапазоне симптомов тревоги по этому опроснику.",
        },
        recommendations: {
          low: [
            "Сейчас вы не отмечаете много симптомов, связанных с тревогой.",
            "Продолжайте здоровые привычки, которые поддерживают ваше благополучие.",
            "Изучите наш контент об устойчивости мозга и профилактике стресса для поддержания психического здоровья.",
          ],
          mild: [
            "Многие люди испытывают это в стрессовые периоды, но полезно следить за своим состоянием.",
            "Попробуйте простые дыхательные упражнения или техники заземления, когда замечаете нарастающую тревогу.",
            "Прочитайте наши статьи о понимании беспокойства и стрессовой реакции.",
            "Если тревога длится долго или причиняет страдания, рассмотрите разговор со специалистом.",
          ],
          moderate: [
            "Это может означать, что тревога занимает много ментального пространства.",
            "Мы рекомендуем вам поговорить со специалистом о том, что вы переживаете.",
            "Создание сети поддержки с друзьями, семьёй или группами помощи может быть полезным.",
            "Изучите техники релаксации и практики успокоения нервной системы.",
          ],
          high: [
            "Это указывает на то, что вы испытываете значительную тревогу, которая может мешать повседневной жизни.",
            "Пожалуйста, поговорите с врачом или специалистом по психическому здоровью в ближайшее время.",
            "Тревога хорошо поддаётся лечению, и эффективные терапии и поддержка доступны.",
            "А пока практикуйте мягкий уход за собой и обращайтесь к близким людям.",
          ],
        },
        nextSteps: {
          articlesTitle: "Читать статьи",
          articlesDesc: "Узнайте о тревоге, стрессовой реакции и техниках успокоения",
          gamesTitle: "Попробовать игры",
          gamesDesc: "Практикуйте внимание и осознанность с помощью мягких упражнений",
        },
      },
      
      // PSS-10
      pss10: {
        title: "PSS-10 – Проверка стресса",
        intro: "За последний месяц как часто вы...",
        resultTitle: "Результат проверки стресса (PSS-10)",
        items: [
          "Расстраивались из-за чего-то неожиданного?",
          "Чувствовали, что не можете контролировать важные вещи в своей жизни?",
          "Чувствовали нервозность и стресс?",
          "Чувствовали уверенность в своей способности справляться с личными проблемами?",
          "Чувствовали, что всё идёт по плану?",
          "Обнаруживали, что не справляетесь со всем, что нужно было сделать?",
          "Были в состоянии контролировать раздражающие факторы в жизни?",
          "Чувствовали, что контролируете ситуацию?",
          "Злились из-за вещей, которые были вне вашего контроля?",
          "Чувствовали, что трудности накапливаются настолько, что вы не можете их преодолеть?",
        ],
        categories: {
          low: "Низкий воспринимаемый стресс",
          moderate: "Умеренный воспринимаемый стресс",
          high: "Высокий воспринимаемый стресс",
        },
        descriptions: {
          low: "Ваш балл находится в нижнем диапазоне воспринимаемого стресса за последний месяц.",
          moderate: "Ваш балл находится в умеренном диапазоне воспринимаемого стресса.",
          high: "Ваш балл находится в верхнем диапазоне воспринимаемого стресса по этой шкале.",
        },
        recommendations: {
          low: [
            "Сейчас вы относительно хорошо справляетесь с жизненными требованиями.",
            "Продолжайте поддерживать рутины и привычки устойчивости.",
            "Изучите наш контент о защитном для мозга образе жизни: сон, движение и когнитивная вовлечённость.",
          ],
          moderate: [
            "Это типично, когда жизнь насыщена или требовательна.",
            "Рассмотрите небольшие конкретные шаги по управлению стрессом: планирование перерывов, установление границ и короткие практики расслабления.",
            "Обратите внимание, начинает ли стресс влиять на сон, настроение или концентрацию.",
            "Если стресс постоянный или подавляющий, рассмотрите разговор со специалистом.",
          ],
          high: [
            "Это указывает на то, что в последнее время жизнь казалась довольно перегруженной.",
            "Мы настоятельно рекомендуем рассмотреть профессиональную поддержку (врач, психотерапевт, консультант).",
            "Постарайтесь снизить нагрузку где возможно: попросите о помощи, делегируйте задачи и расставьте приоритеты.",
            "Практикуйте сострадание к себе и помните, что высокий стресс – это сигнал к действию.",
            "Изучите наши ресурсы о стрессе, выгорании и восстановлении.",
          ],
        },
        disclaimerNote: "Он измеряет ваше восприятие стресса, которое субъективно и может меняться изо дня в день.",
        nextSteps: {
          articlesTitle: "Читать статьи",
          articlesDesc: "Узнайте о стрессе, устойчивости мозга и стратегиях восстановления",
          gamesTitle: "Попробовать игры",
          gamesDesc: "Маленькие шаги к спокойному уму с помощью увлекательных упражнений",
        },
      },
      
      // MAAS
      maas: {
        title: "MAAS – Проверка осознанности",
        intro: "Пожалуйста, укажите, как часто или редко у вас в настоящее время бывает каждый из этих опытов.",
        resultTitle: "Проверка осознанности (MAAS)",
        items: [
          "Я могу испытывать какую-то эмоцию и не осознавать её до какого-то момента позже.",
          "Я ломаю или проливаю вещи из-за неосторожности, невнимательности или потому что думаю о чём-то другом.",
          "Мне трудно сосредоточиться на том, что происходит в настоящем.",
          "Я склонен(на) быстро идти к цели, не обращая внимания на то, что испытываю по пути.",
          "Я склонен(на) не замечать чувства физического напряжения или дискомфорта, пока они действительно не привлекут моё внимание.",
          "Я забываю имя человека почти сразу после того, как мне его назвали.",
          "Кажется, что я действую «на автопилоте», мало осознавая, что делаю.",
          "Я спешу с делами, не будучи по-настоящему внимательным к ним.",
          "Я так сосредоточен(а) на цели, которую хочу достичь, что теряю связь с тем, что делаю прямо сейчас для её достижения.",
          "Я выполняю работу или задачи автоматически, не осознавая, что делаю.",
          "Я обнаруживаю, что слушаю кого-то одним ухом, одновременно делая что-то другое.",
          "Я еду куда-то «на автопилоте» и потом удивляюсь, зачем я туда поехал(а).",
          "Я обнаруживаю себя погружённым(ой) в мысли о будущем или прошлом.",
          "Я обнаруживаю, что делаю что-то, не обращая внимания.",
          "Я перекусываю, не осознавая, что ем.",
        ],
        categories: {
          low: "Низкая повседневная осознанность",
          moderate: "Умеренная повседневная осознанность",
          high: "Высокая повседневная осознанность",
        },
        descriptions: {
          low: "Ваши ответы указывают на то, что вы часто находитесь «на автопилоте» или отвлечены от настоящего момента.",
          moderate: "Ваши ответы находятся в среднем диапазоне повседневной осознанности. У вас есть моменты присутствия и моменты автоматического пилотирования – это очень распространено.",
          high: "Ваши ответы указывают на то, что вы часто осознаёте, что происходит в настоящий момент. Этот уровень осознанности может поддерживать эмоциональный баланс и здоровье мозга.",
        },
        recommendations: {
          low: [
            "Это очень распространено в нашей напряжённой жизни, и здесь нет осуждения – это просто информация.",
            "Попробуйте короткие, доступные практики осознанности (1–3 минуты осознанного дыхания или замечания ощущений).",
            "Включайте маленькие моменты осознанности в повседневные дела – например, замечайте ощущение воды, когда моете руки.",
            "Изучите наши статьи об осознанности и упражнения на заземление.",
          ],
          moderate: [
            "Рассмотрите возможность развития более осознанного присутствия в повседневной жизни.",
            "Попробуйте простой еженедельный челлендж: одно осознанное занятие в день (осознанная ходьба, еда или слушание).",
            "Прочитайте наши статьи о том, как осознанность поддерживает устойчивость мозга, управление стрессом и внимание.",
            "Практикуйте любопытство вместо критики, когда ваш ум блуждает.",
          ],
          high: [
            "Продолжайте поддерживать эти привычки – они защищают ваше благополучие.",
            "Рассмотрите изучение более продвинутых или игровых упражнений осознанности (осознанное слушание, осознанное общение).",
            "Поделитесь своей практикой с другими, кому это может быть полезно.",
            "Попробуйте наши игры на внимание, которые тренируют присутствие в моменте.",
          ],
        },
        disclaimerNote: "Этот результат предназначен для саморефлексии и личного развития, а не для клинической оценки или диагноза. MAAS измеряет осознанность в настоящий момент в повседневной жизни. Это один из многих инструментов для понимания вашего опыта.",
        nextStepsTitle: "Маленькие шаги к более спокойному, присутствующему уму",
        nextSteps: {
          articlesTitle: "Статьи об осознанности",
          articlesDesc: "Узнайте о внимании, устойчивости мозга и присутствии в моменте",
          gamesTitle: "Игры на внимание",
          gamesDesc: "Тренируйте фокус и присутствие с помощью увлекательных упражнений",
        },
      },
      
      // PSQI
      psqi: {
        title: "PSQI – Проверка качества сна",
        intro: "Следующие вопросы относятся только к вашим обычным привычкам сна за последний месяц.",
        resultTitle: "Проверка качества сна (PSQI)",
        questions: {
          bedtime: "За последний месяц, во сколько вы обычно ложились спать вечером?",
          fallAsleep: "За последний месяц, сколько минут обычно вам требовалось, чтобы заснуть каждую ночь?",
          wakeTime: "За последний месяц, во сколько вы обычно вставали утром?",
          sleepHours: "За последний месяц, сколько часов фактического сна вы получали ночью? (Это может отличаться от количества часов, проведённых в постели.)",
          qualityOverall: "За последний месяц, как бы вы оценили качество своего сна в целом?",
          troubleSleepWithin30: "За последний месяц, как часто у вас были проблемы со сном из-за того, что вы не могли заснуть в течение 30 минут?",
          wakeUpMiddle: "За последний месяц, как часто у вас были проблемы со сном из-за того, что вы просыпались среди ночи или рано утром?",
          medication: "За последний месяц, как часто вы принимали лекарства (по рецепту или без), чтобы помочь себе заснуть?",
          troubleStayingAwake: "За последний месяц, как часто у вас были проблемы с бодрствованием во время вождения, приёма пищи или социальной активности?",
          enthusiasm: "За последний месяц, насколько сложно вам было сохранять достаточно энтузиазма для выполнения дел?",
        },
        qualityOptions: {
          veryGood: "Очень хорошо",
          fairlyGood: "Довольно хорошо",
          fairlyBad: "Довольно плохо",
          veryBad: "Очень плохо",
        },
        frequencyOptions: {
          notDuringPastMonth: "Ни разу за последний месяц",
          lessThanOnce: "Реже одного раза в неделю",
          onceOrTwice: "Один или два раза в неделю",
          threeOrMore: "Три или более раз в неделю",
        },
        problemOptions: {
          noProblem: "Никаких проблем",
          slightProblem: "Только очень небольшая проблема",
          somewhatProblem: "В некоторой степени проблема",
          bigProblem: "Очень большая проблема",
        },
        enterNumber: "Введите число",
        categories: {
          good: "Хорошее качество сна",
          some: "Некоторые проблемы со сном",
          significant: "Значительные проблемы со сном",
        },
        descriptions: {
          good: "Ваш балл находится в диапазоне, обычно связанном с лучшим качеством сна. Иногда у вас могут быть плохие ночи – это нормально – но в целом этот опросник не выявляет серьёзных проблем со сном.",
          some: "Ваш балл указывает на то, что в последнее время вы испытывали некоторые проблемы со сном. Это может включать более длительное время засыпания, ночные пробуждения или дневную усталость.",
          significant: "Ваш балл находится в диапазоне, где многие люди обнаруживают, что проблемы со сном заметно влияют на их дневную энергию, настроение или концентрацию.",
        },
        recommendations: {
          good: [
            "Поддерживайте здоровые привычки сна: постоянный режим, воздействие света днём и движение.",
            "Держите спальню прохладной, тёмной и тихой.",
            "Изучите наш контент о сне для долгосрочной защиты здоровья сна.",
            "Помните, что стресс или жизненные изменения могут временно влиять на сон.",
          ],
          some: [
            "Попробуйте простые изменения гигиены сна: ритуал подготовки ко сну, уменьшение экранов перед сном, регулярное время пробуждения.",
            "Ведите дневник сна в течение недели, чтобы выявить закономерности.",
            "Изучите наши упражнения на расслабление перед сном.",
            "Если проблемы со сном сохраняются или усугубляются, рассмотрите разговор со специалистом.",
            "Проверьте, влияют ли стресс, беспокойство или физический дискомфорт на ваш сон.",
          ],
          significant: [
            "Мы настоятельно рекомендуем обсудить ваши проблемы со сном с врачом или специалистом по сну.",
            "Проблемы со сном распространены и поддаются лечению – вам не нужно принимать плохой сон как «норму».",
            "Изучите когнитивно-поведенческую терапию бессонницы (КПТ-И), которая очень эффективна.",
            "Прочитайте наши статьи о бессоннице, циркадных ритмах и взаимодействии стресса и сна.",
            "А пока поддерживайте постоянное время пробуждения и избегайте долгого дневного сна.",
          ],
        },
        disclaimerNote: "Он измеряет ваше субъективное качество сна, что важно, но не заменяет клиническую оценку сна. Если проблемы со сном постоянны или серьёзны, или если вы подозреваете расстройство сна (например, апноэ), пожалуйста, обратитесь к специалисту.",
        nextStepsTitle: "Маленькие эксперименты для мягкого улучшения сна",
        nextSteps: {
          articlesTitle: "Статьи о сне",
          articlesDesc: "Узнайте о гигиене сна, циркадных ритмах и техниках расслабления",
          gamesTitle: "Успокаивающие упражнения",
          gamesDesc: "Попробуйте мягкие занятия для дневного функционирования и ночного расслабления",
        },
      },
      
      // UCLA
      ucla: {
        title: "Шкала одиночества UCLA – Проверка связей",
        intro: "Пожалуйста, укажите, как часто вы чувствуете то, что описано в каждом утверждении.",
        resultTitle: "Проверка связей (Шкала одиночества UCLA)",
        items: [
          "Как часто вы чувствуете, что находитесь «на одной волне» с окружающими?",
          "Как часто вы чувствуете, что вам не хватает общения?",
          "Как часто вы чувствуете, что вам не к кому обратиться?",
          "Как часто вы чувствуете себя одиноким(ой)?",
          "Как часто вы чувствуете себя частью группы друзей?",
          "Как часто вы чувствуете, что у вас много общего с окружающими?",
          "Как часто вы чувствуете, что больше ни с кем не близки?",
          "Как часто вы чувствуете, что ваши интересы и идеи не разделяют окружающие?",
          "Как часто вы чувствуете себя общительным(ой) и дружелюбным(ой)?",
          "Как часто вы чувствуете близость к людям?",
          "Как часто вы чувствуете себя забытым(ой)?",
          "Как часто вы чувствуете, что ваши отношения с другими не имеют смысла?",
          "Как часто вы чувствуете, что никто по-настоящему вас не знает?",
          "Как часто вы чувствуете себя изолированным(ой) от других?",
          "Как часто вы чувствуете, что можете найти компанию, когда хотите?",
          "Как часто вы чувствуете, что есть люди, которые вас действительно понимают?",
          "Как часто вы чувствуете застенчивость?",
          "Как часто вы чувствуете, что люди рядом, но не с вами?",
          "Как часто вы чувствуете, что есть люди, с которыми вы можете поговорить?",
          "Как часто вы чувствуете, что есть люди, к которым вы можете обратиться?",
        ],
        categories: {
          low: "Низкий уровень чувства одиночества",
          moderate: "Умеренное чувство одиночества",
          high: "Высокий уровень чувства одиночества",
        },
        descriptions: {
          low: "Ваши ответы указывают на то, что в целом вы обычно чувствуете себя достаточно связанным(ой) с другими. Каждый может иногда чувствовать одиночество, и это совершенно нормально.",
          moderate: "Ваши ответы указывают на то, что вы испытывали некоторые чувства одиночества или разобщённости. Это очень распространено, особенно во время переходных периодов, высокого стресса или больших жизненных изменений.",
          high: "Ваши ответы указывают на то, что одиночество было довольно заметным в вашей жизни в последнее время. Чувство изоляции может быть эмоционально тяжёлым и со временем влиять на здоровье мозга и тела.",
        },
        recommendations: {
          low: [
            "Поддерживайте значимые отношения и общественную деятельность, которые приносят вам радость.",
            "Проявляйте любопытство к углублению существующих связей, а не только к поиску новых.",
            "Изучите наш контент о социальной вовлечённости и здоровье мозга.",
            "Помните, что качество связей важнее количества.",
          ],
          moderate: [
            "Рассмотрите небольшие реалистичные шаги: обратиться к одному человеку, присоединиться к хобби-группе или изучить онлайн-сообщества, которые кажутся безопасными.",
            "Помните, что одиночество – это не недостаток характера, а сигнал, как голод или жажда.",
            "Прочитайте наши статьи о построении социальных связей и преодолении социальной изоляции.",
            "Если одиночество постоянно и болезненно, рассмотрите разговор с психотерапевтом или консультантом.",
            "Волонтёрская работа или структурированные социальные мероприятия могут обеспечить регулярное общение.",
          ],
          high: [
            "Мы настоятельно рекомендуем обратиться за поддержкой: к близким друзьям/семье, группам поддержки или специалисту по психическому здоровью.",
            "Терапия (особенно подходы, ориентированные на отношения и социальную тревогу) может быть очень полезной.",
            "Рассмотрите структурированные занятия, которые обеспечивают регулярное общение: курсы, группы, волонтёрство.",
            "Маленькие шаги считаются: даже одно значимое взаимодействие в неделю – это прогресс.",
            "Изучите наши ресурсы о связях, социальном здоровье и одиночестве.",
            "Помните, что эти чувства обоснованы, и изменения возможны при поддержке.",
          ],
        },
        disclaimerNote: "Этот результат описывает, как вы ответили на эту шкалу; это не диагноз. Одиночество – это субъективный опыт, и этот опросник отражает ваше восприятие в данный момент времени. Если одиночество кажется подавляющим или влияет на повседневную жизнь, разговор со специалистом может помочь.",
        nextStepsTitle: "Способы чувствовать себя немного менее одиноким со временем",
        nextSteps: {
          articlesTitle: "Статьи о связях",
          articlesDesc: "Узнайте о социальном здоровье, построении связей и преодолении изоляции",
          gamesTitle: "Социальные активности",
          gamesDesc: "Изучите занятия, включающие общение и совместный опыт",
        },
      },
      
      // Response options
      responses: {
        notAtAll: "Совсем нет",
        severalDays: "Несколько дней",
        moreThanHalf: "Более половины дней",
        nearlyEvery: "Почти каждый день",
        
        never: "Никогда",
        almostNever: "Почти никогда",
        sometimes: "Иногда",
        fairlyOften: "Довольно часто",
        veryOften: "Очень часто",
        
        almostAlways: "Почти всегда",
        veryFrequently: "Очень часто",
        somewhatFrequently: "Довольно часто",
        somewhatInfrequently: "Довольно редко",
        veryInfrequently: "Очень редко",
        almostNeverMaas: "Почти никогда",
        
        rarely: "Редко",
        often: "Часто",
      },
      
      // Common results text
      importantDisclaimer: "Важное замечание",
      screeningTool: "Этот опросник является инструментом скрининга и саморефлексии, а не диагнозом.",
      cannotTell: "Он не может точно сказать, есть ли у вас психическое расстройство.",
      ifSymptomsWorry: "Если ваши симптомы беспокоят вас, мешают повседневной жизни или ваш балл находится в высоком диапазоне, рассмотрите разговор с врачом, психологом или другим квалифицированным специалистом.",
      
      crisisResources: "Кризисные ресурсы",
      crisisText: "Если вы думаете о причинении себе вреда или чувствуете непосредственную опасность, пожалуйста, немедленно обратитесь за помощью:",
      crisisContacts: [
        "Свяжитесь с местными экстренными службами (112 или 911)",
        "Позвоните на местную линию кризисной или суицидальной помощи",
        "Обратитесь в ближайшее отделение неотложной помощи",
      ],
      
      readArticles: "Читать статьи",
      tryGames: "Попробовать игры",
    },
    
    // Photo Booth
    photoBooth: {
      title: "Ретро-фотокабина",
      subtitle: "Инструмент для запечатления моментов: четыре быстрых снимка, одна фотополоска на память.",
      vintageSubtitle: "Четыре быстрых снимка, одна фотополоска на память.",
      description: "Загляните в виртуальную фотокабину. Когда будете готовы, начните сессию, следите за обратным отсчётом, и камера сделает четыре снимка с небольшими паузами. В конце ваши фото появятся в виде ретро-полоски, которую можно скачать и сохранить.",
      backToGames: "Назад",
      boothTitle: "Ретро-фотокабина",
      readyMessage: "Приготовьтесь – 4 фото подряд",
      cameraError: "Похоже, мы не можем получить доступ к вашей камере. Пожалуйста, проверьте настройки браузера или попробуйте другое устройство.",
      tryAgain: "Попробовать снова",
      photoOf: "Фото {current} из {total}",
      processing: "Создаём вашу фотополоску...",
      startSession: "Начать фотосессию",
      download: "Скачать фотополоску",
      downloadStrip: "Скачать полоску",
      retake: "Переснять",
      privacyNote: "Ваши фото остаются на устройстве – мы их не храним.",
      privacyNotice: "Мы не храним ваши фото – они остаются на вашем устройстве, если вы их не скачаете.",
      capturing: "Не двигайтесь, снимаем...",
      printing: "Печатаем ваши фото...",
    },
    
    // Sampler page
    sampler: {
      title: "Твоя персональная подборка",
      subtitle: "Собрано специально для тебя ✨ (можно перемешать)",
      savedNote: "Сохранено на этом устройстве.",
      shuffleAll: "Перемешать всё",
      open: "Открыть",
      shuffle: "Другой",
      more: "Ещё",
      backToHome: "На главную",
      back: "Назад",
      showMeBtn: "Удиви меня",
      
      // Category names
      categories: {
        game: "Игра",
        exercise: "Упражнение",
        cafe: "OMG Кафе",
        questionnaire: "Опросник",
        article: "Статья",
        photobooth: "Фотобудка",
      },
      
      // Game items
      games: {
        colour: { title: "Цвет и Слово", desc: "Сопоставляйте цвета, игнорируя текст" },
        number: { title: "Числовое Эхо", desc: "Запоминайте и повторяйте последовательности чисел" },
        focustap: { title: "Фокус-Тап", desc: "Быстро и точно нажимайте на цели" },
        memorygrid: { title: "Сетка Памяти", desc: "Запоминайте узоры на сетке" },
        mathminute: { title: "Минута Математики", desc: "Решайте задачи на время" },
        wordsprint: { title: "Словесный Спринт", desc: "Печатайте слова как можно быстрее" },
        reaction: { title: "Реакция", desc: "Проверьте скорость реакции" },
        memorycard: { title: "Найди Пару", desc: "Найдите совпадающие пары карточек" },
        numbersequence: { title: "Числовой Путь", desc: "Нажимайте числа от 1 до 25 по порядку" },
        targetshift: { title: "Сдвиг Прицела", desc: "Мозг перенастраивает движения" },
        changeblindness: { title: "Слепота к Изменениям", desc: "Найдите, что изменилось после вспышки" },
      },
      
      // Exercise items
      exercises: {
        boxbreathing: { title: "Квадратное Дыхание", desc: "Успокаивающая техника дыхания 4-4-4-4" },
        awarenesscheckin: { title: "Проверка Осознанности", desc: "Соединитесь со своим телом и разумом" },
        fivesensegrounding: { title: "5 Чувств", desc: "Заземлитесь с помощью пяти чувств" },
        panicsos: { title: "SOS при Панике", desc: "Быстрая помощь в тяжёлые моменты" },
        nighttimeworry: { title: "Парковка Ночных Тревог", desc: "Отпустите тревоги перед сном" },
        cognitiveshuffle: { title: "Когнитивная Перетасовка", desc: "Мягкая перезагрузка, когда мысли зацикливаются" },
        eisenhowermatrix: { title: "Матрица Эйзенхауэра", desc: "Приоритизируйте задачи по срочности и важности" },
        ambientstudio: { title: "Студия звуков", desc: "Создай свой звуковой фон из звуков природы и города" },
      },
      
      // Cafe
      cafe: { title: "OMG Кафе", desc: "Соберите уютный напиток и тренируйте память" },
      
      // Questionnaire items
      questionnaires: {
        phq9: { title: "PHQ-9 Скрининг Депрессии", desc: "Оценка симптомов депрессии" },
        gad7: { title: "GAD-7 Скрининг Тревоги", desc: "Измерение уровня тревожности" },
        pss10: { title: "PSS-10 Шкала Стресса", desc: "Оценка воспринимаемого стресса" },
        maas: { title: "MAAS Шкала Осознанности", desc: "Измерение осознанности" },
        psqi: { title: "PSQI Качество Сна", desc: "Оценка качества сна" },
        ucla: { title: "UCLA Шкала Одиночества", desc: "Измерение чувства одиночества" },
        cbi: { title: "CBI Проверка на Выгорание", desc: "Оценка личного и рабочего выгорания" },
        criq: { title: "CRIq Запас Мозга", desc: "Изучите факторы когнитивного резерва" },
      },
      
      // Article items
      articles: {
        "brain-and-mind": { title: "Мозг и Разум", desc: "Изучите основы нейронауки" },
        dementia: { title: "Деменция", desc: "Понимание потери памяти и когнитивного спада" },
        parkinsons: { title: "Болезнь Паркинсона", desc: "Движение, дофамин и мозг" },
        adhd: { title: "СДВГ", desc: "Внимание, фокус и исполнительные функции" },
        sleep: { title: "Сон", desc: "Наука об отдыхе и восстановлении" },
        autism: { title: "Аутизм", desc: "Нейроразнообразие и уникальные перспективы" },
        schizophrenia: { title: "Шизофрения", desc: "Восприятие, реальность и разум" },
        anxiety: { title: "Тревожность", desc: "Понимание страха и беспокойства" },
        depression: { title: "Депрессия", desc: "Настроение, мотивация и мозг" },
        ptsd: { title: "ПТСР", desc: "Травма, память и исцеление" },
        "feelings-emotions": { title: "Чувства и Эмоции", desc: "Наука о том, что мы чувствуем" },
        "pain-nociception": { title: "Боль и Ноцицепция", desc: "Как мы воспринимаем и обрабатываем боль" },
        "sense-organs": { title: "Органы Чувств", desc: "Как мы воспринимаем мир" },
        microbiota: { title: "Микробиота и Нервная Система", desc: "Как микробы кишечника общаются с мозгом" },
        "coffee-tea": { title: "Кофе, Чай и Мозг", desc: "Кофеин, полифенолы и здоровье мозга" },
      },
      
      // Photo booth
      photobooth: { title: "Фотобудка", desc: "Инструмент для запечатления моментов" },
      
      // Time labels
      times: {
        "1min": "1 мин",
        "1-2min": "1-2 мин",
        "2-3min": "2-3 мин",
        "3-5min": "3-5 мин",
        "5min": "5 мин",
        "5-7min": "5-7 мин",
        "5-10min": "5-10 мин",
        "8-10min": "8-10 мин",
        "10-12min": "10-12 мин",
        "12-15min": "12-15 мин",
        justForFun: "Просто для веселья",
      },
    },
    
    // Next Steps cross-linking
    nextSteps: {
      title: "Что дальше",
      openButton: "Открыть",
      categoryLabels: {
        article: "Статья",
        game: "Игра",
        exercise: "Упражнение",
        questionnaire: "Опросник",
      },
    },
  },
  de,
} as const;
